/**
 * Ballast PWA — shell, router and views.
 *
 * Four destinations, all real: Canvas (the envelope grid and tap-to-fund),
 * Needs You, Accounts and Settings. The Now-Bar drives them.
 *
 * TWO RULES THAT RUN THROUGH EVERY VIEW:
 *
 * 1. NEVER INVENT A NUMBER. A balance we could not fetch, or that the bank has
 *    not reported, renders as an em dash and says why — never as 0, and never
 *    as a stale figure presented as current. A fabricated "safe to spend" in a
 *    cash-allocation app is the same dangerous lie as a stale sync (§14).
 *
 * 2. NEVER RENDER A DEAD CONTROL. If there is nothing behind a button it is not
 *    drawn. The focal alert omits its action when no flow exists yet; a tile
 *    with no handler renders as a plain element rather than a disabled button.
 *    One control that does nothing teaches the operator that none of them work.
 *
 * The runway gauge (Slice 3) is drawn only when the server has a runway
 * figure. Until a bank has supplied income history it has none, and the card
 * says so with an em dash rather than a dial pointing at zero.
 */

import {
  initTheme,
  watchSystemTheme,
  applyTheme,
  readPreference,
  type ThemePreference,
} from './lib/theme';
import {
  api,
  envelopeApi,
  proposalApi,
  depositApi,
  ApiError,
  type ApiEnvelope,
  type ApiProposal,
  type ApiDeposit,
  type ApiInflowType,
  type EnvelopesResponse,
  billApi,
  type ApiBill,
  type ApiPaidBill,
  type BillsResponse,
  bankApi,
  connectionApi,
  type ApiAccount,
  type BankSyncResult,
  type ConnectionSummary,
  type MeResponse,
  receiptApi,
  type ApiTransaction,
  onboardingApi,
  pushApi,
  type PushInfo,
  type PushPrefs,
} from './lib/api';
import { createBusinessSheet } from './components/business-sheet';
import { onboardingSteps, showOnboarding, type OnboardingStep } from './lib/onboarding';
import {
  ensureSubscription,
  pushStatus,
  turnOff,
  turnOn,
  type DeviceFlag,
  type PushManagerLike,
  type PushServer,
} from './lib/push-client';
import { createTransactionsCard, type TransactionsView } from './components/transactions-card';
import { createReceiptSheet } from './components/receipt-sheet';
import { PARSE_MESSAGE, parseMoneyToMinor } from './lib/money-input';
import { createZoneGrid } from './components/zone-grid';
import { CHOOSABLE_ZONES, defaultZoneFor, groupIntoZones } from './lib/zones';
import { createFundSheet } from './components/fund-sheet';
import { enableDragToFund } from './lib/drag-fund';
import { formatMoney, type EnvelopeTileModel } from './lib/envelope-math';
import { createFocalAlert } from './components/focal-alert';
import { createGauge } from './components/gauge';
import { parsePercentToPpm, ppmToPercentText } from './lib/rates';
import { connectBank, reconnectBank } from './lib/plaid-link';
import { createFreshness, computeFreshness } from './components/freshness';
import { createNowBar, type NavKey } from './components/nowbar';
import { loginErrorText } from './lib/login-errors';
import { createProposalCard } from './components/proposal-card';
import { createProposalEditSheet } from './components/proposal-edit-sheet';
import { createPasswordSheet } from './components/password-sheet';
import { createPasskeySheet, RestartEnrolment } from './components/passkey-sheet';
import { passkeyApi, type PasskeyList, type PasskeySummary } from './lib/api';
import {
  authenticationJSON,
  creationOptions,
  passkeyEnrolErrorText,
  passkeyMeta,
  passkeySignInErrorText,
  passkeysSupported,
  registrationJSON,
  requestOptions,
  SIGN_IN_OPTIONS_MAX_AGE_MS,
  type CreationOptionsJSON,
  type CredentialLike,
  type RequestOptionsJSON,
} from './lib/passkey-client';
import { passwordChangeErrorText, passwordChangedText } from './lib/password-errors';
import { trapFocus } from './lib/dialog-trap';
import { isValidDateString, relativeDateChoices, dueText } from './lib/due-date';
import { nextObligation, obligationCopy } from './lib/attention';
import { queuePillText } from './lib/proposal-copy';
import { createDepositCard } from './components/deposit-card';
import { createBillCard, createForwardCard, createPaidBillCard } from './components/bill-card';
import { forwardView } from './lib/forward';
import { createRecentDeposits } from './components/deposits-list';
import { createFillOrderSheet } from './components/fill-order-sheet';
import { classifyMessage, depositsNeedingALook, needsYouCount } from './lib/deposit-copy';
import { fillOrder } from './lib/fill-order';
import { describeSync, runManualSync, type BankAsk, type SyncPhase } from './lib/manual-sync';
import { createDisconnectSheet } from './components/disconnect-sheet';
import { trackWindowHeight } from './lib/viewport';
import {
  anyImporting,
  BANKS_UNREACHABLE,
  canReconnect,
  describeBankSync,
  historyNote,
  needsReconnect,
  shouldRefreshOnReturn,
} from './lib/connection-copy';

export const VERSION = '11.0.1';

const app = document.getElementById('app')!;

function clear(node: HTMLElement) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/* -------------------------------------------------------------------------
 * Signed-out view
 * ---------------------------------------------------------------------- */

function renderLogin(onSuccess: () => void) {
  clear(app);

  const scroll = h('div', 'scroll');
  const body = h('div', 'body');

  const hero = h('div', 'hero');
  hero.append(h('div', 'safe-label', 'Ballast'), h('div', 'safe-figure', '⚓'));
  hero.append(h('div', 'safe-sub', 'Praeclarus Ventures'));

  const form = h('form', 'card vitals');
  form.setAttribute('novalidate', '');

  const title = h('h2', undefined, 'Sign in');
  title.style.marginBottom = '12px';

  const emailLabel = h('label', 'label', 'Email');
  emailLabel.htmlFor = 'email';
  const email = h('input');
  email.id = 'email';
  email.type = 'email';
  email.autocomplete = 'username';
  email.required = true;

  const pwLabel = h('label', 'label', 'Password');
  pwLabel.htmlFor = 'password';
  const password = h('input');
  password.id = 'password';
  password.type = 'password';
  password.autocomplete = 'current-password';
  password.required = true;

  for (const input of [email, password]) {
    input.style.cssText =
      'width:100%;min-height:var(--tap);padding:10px 12px;margin:6px 0 14px;' +
      'border:1px solid var(--cardln);border-radius:10px;background:transparent;' +
      'color:var(--ink);font:inherit';
  }

  const submit = h('button', undefined, 'Sign in');
  submit.type = 'submit';
  submit.style.cssText =
    'width:100%;min-height:var(--tap);border:0;border-radius:99px;cursor:pointer;' +
    'background:var(--sig-ground);color:var(--sig-ink);font:inherit;font-weight:700';

  const error = h('p', 'soft');
  error.setAttribute('role', 'alert');
  error.style.cssText = 'color:var(--bad);font-size:.85rem;min-height:1.2em;margin:10px 0 0';

  // v11.0.0: filled in only if this host can offer a passkey (no dead controls).
  const passkeyBox = h('div', 'passkey-signin');

  form.append(title, emailLabel, email, pwLabel, password, submit, error, passkeyBox);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    submit.disabled = true;
    submit.textContent = 'Signing in…';
    try {
      await api.login(email.value, password.value);
      onSuccess();
    } catch (err) {
      error.textContent = loginErrorText(err);
      submit.disabled = false;
      submit.textContent = 'Sign in';
    }
  });

  body.append(form);
  scroll.append(hero, body);
  app.append(scroll);
  email.focus();

  // Signed out: whatever was cached belongs to whoever was signed in.
  passkeyList = null;
  passkeyLoad = null;
  passkeyNote = '';
  void offerPasskeySignIn(passkeyBox, onSuccess);
}

/**
 * "Sign in with a passkey" (v11.0.0), under the password form.
 *
 * The challenge is fetched BEFORE the tap and kept fresh while the screen is
 * up, so the tap's first await is the platform call: Safari ties the passkey
 * sheet to the tap, and a round trip first can spend it (passkey-client.ts).
 * A challenge is single use, so a fresh one is fetched after every attempt.
 */
async function offerPasskeySignIn(box: HTMLElement, onSuccess: () => void) {
  if (!passkeysSupported(window)) return;
  try {
    if (!(await passkeyApi.status()).available) return;
  } catch {
    return;
  }
  if (!box.isConnected) return;

  let options: RequestOptionsJSON | null = null;
  let fetchedAt = 0;
  const refresh = async () => {
    try {
      options = await passkeyApi.signInOptions();
      fetchedAt = Date.now();
    } catch {
      options = null;
    }
  };
  await refresh();
  if (!box.isConnected) return;

  const or = h('p', 'soft passkey-or', 'or');
  const button = h('button', 'btn-quiet passkey-signin-button', 'Sign in with a passkey');
  button.type = 'button';
  const note = h('p', 'soft passkey-signin-note');
  note.setAttribute('role', 'alert');
  box.append(or, button, note);

  const timer = setInterval(() => {
    if (!box.isConnected) return clearInterval(timer);
    void refresh();
  }, SIGN_IN_OPTIONS_MAX_AGE_MS);

  button.addEventListener('click', async () => {
    note.textContent = '';
    if (!options || Date.now() - fetchedAt > SIGN_IN_OPTIONS_MAX_AGE_MS) {
      await refresh();
      if (!options) {
        note.textContent = "Couldn't reach Ballast. Check your connection.";
        return;
      }
    }
    const current = options;
    options = null;
    button.disabled = true;
    button.textContent = 'Waiting for your passkey…';
    try {
      // FIRST await after the tap (see above).
      const cred = await navigator.credentials.get(requestOptions(current));
      if (!cred) throw new DOMException('No passkey was chosen.', 'NotAllowedError');
      await passkeyApi.signIn(authenticationJSON(cred as unknown as CredentialLike));
      clearInterval(timer);
      onSuccess();
    } catch (err) {
      note.textContent = passkeySignInErrorText(err) ?? '';
      button.disabled = false;
      button.textContent = 'Sign in with a passkey';
      void refresh();
    }
  });
}

/* -------------------------------------------------------------------------
 * App state + router
 *
 * One state object, one render(). The Now-Bar's four destinations are real
 * views: an app whose primary navigation does nothing is not a shell, it is a
 * screenshot.
 * ---------------------------------------------------------------------- */

/**
 * The money model is THREE states, not two.
 *
 * `null` used to mean both "no entity yet" and "the request failed", and the
 * shell rendered both as a permanent "Loading…" under a green "Nothing needs
 * you" pill — the app confidently reporting all-clear about figures it had
 * failed to fetch. That is the same class of lie as a stale balance shown as
 * fresh, and it is the one thing this app exists not to do.
 */
type MoneyState =
  | { status: 'ok'; data: EnvelopesResponse }
  | { status: 'none' }
  | { status: 'error'; message: string };

/**
 * The Needs You queue, with the same three states and for the same reason.
 *
 * A failed fetch must NOT render as an empty queue: "nothing needs you" is a
 * claim, and making it out of a request that did not come back is the same
 * confident lie as a fabricated balance.
 */
type QueueState =
  | {
      status: 'ok';
      proposals: ApiProposal[];
      /**
       * Recent deposits, answered or not. The ones with no answer are part of
       * this queue (one queue, never two — §13); the rest are the record.
       */
      deposits: ApiDeposit[];
      /**
       * v8.0.0: bills the bank sees repeating ("track it?") and tracked bills it
       * has seen paid ("clear and roll forward"). Same queue (§13).
       */
      bills: BillsResponse;
    }
  | { status: 'none' }
  | { status: 'error'; message: string };

/** Whether "Recent deposits" is open — it survives the repaint after each answer. */
let recentDepositsOpen = false;

/** Everything in the queue that needs the operator: suggestions and open questions. */
function queueCount(q: Extract<QueueState, { status: 'ok' }>): number {
  return needsYouCount(
    q.proposals.length,
    depositsNeedingALook(q.deposits).length,
    q.bills.suggestions.length + q.bills.paid.length,
  );
}

interface AppState {
  me: MeResponse;
  view: NavKey;
  /** Which entity the Canvas is showing. Entities never commingle (§3). */
  entityId: string | null;
  money: MoneyState;
  queue: QueueState;
  /** Proposal ids with a decision in flight, so a second tap cannot fire. */
  deciding: Set<string>;
  /** Every linked account, for the assignment screen. NULL until loaded. */
  accounts: ApiAccount[] | null;
  /**
   * Epoch ms of the last time every figure on screen was successfully loaded
   * from the Worker. Settings reads it so the operator can see how old the
   * screen is without having to trust that it is current.
   */
  loadedAt: number;
}

let state: AppState | null = null;

/**
 * Transactions for the receipts card (v9.0.0), for ONE entity. Kept out of
 * AppState and out of `refresh()`'s reads on purpose: it is loaded only when
 * the Accounts screen is shown, so Sync now's exact set of reads is unchanged
 * (e2e/sync.spec.ts). `refresh()` drops it, so the next visit re-reads it.
 */
let txns: { entityId: string; view: TransactionsView } | null = null;

async function loadTransactions(entityId: string, more = false) {
  const prior = txns?.entityId === entityId && txns.view.status === 'ok' ? txns.view : null;
  if (more && prior) {
    txns = { entityId, view: { ...prior, loadingMore: true } };
  } else {
    txns = { entityId, view: { status: 'loading' } };
  }
  let view: TransactionsView;
  try {
    const page = await receiptApi.transactions(entityId, more ? prior?.data.next : undefined);
    view = {
      status: 'ok',
      loadingMore: false,
      data:
        more && prior
          ? { ...page, transactions: [...prior.data.transactions, ...page.transactions] }
          : page,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    view =
      more && prior
        ? { ...prior, loadingMore: false }
        : {
            status: 'error',
            message:
              err instanceof ApiError ? err.message : "Couldn't load transactions. Try Sync now.",
          };
  }
  // The operator may have switched entity while this was in flight.
  if (txns?.entityId !== entityId) return;
  txns = { entityId, view };
  repaintTransactions(entityId);
}

function transactionsCard(entityId: string): HTMLElement {
  return createTransactionsCard({
    view: txns!.view,
    onOpen: (t) => openReceiptSheet(entityId, t),
    onMore: () => void loadTransactions(entityId, true),
  });
}

function renderTransactions(entityId: string): HTMLElement {
  if (!txns || txns.entityId !== entityId) void loadTransactions(entityId);
  return transactionsCard(entityId);
}

/**
 * Swap in a fresh Transactions card — and ONLY that card. Calling render()
 * here rebuilt all of Accounts whenever the list arrived, which wiped a
 * "Disconnected …" message written a moment earlier and detached controls
 * under the operator's finger (found by the full browser run; pinned in
 * e2e/receipts.spec.ts).
 */
function repaintTransactions(entityId: string) {
  if (state?.view !== 'accounts' || txns?.entityId !== entityId) return;
  document.querySelector('section.transactions')?.replaceWith(transactionsCard(entityId));
}

function openReceiptSheet(entityId: string, transaction: ApiTransaction) {
  const view = txns?.entityId === entityId && txns.view.status === 'ok' ? txns.view : null;
  if (!view) return;
  const sheet = createReceiptSheet({
    entityId,
    transaction,
    receiptsEnabled: view.data.receiptsEnabled,
    limits: view.data.limits,
    onChanged: (t) => {
      if (txns?.entityId !== entityId || txns.view.status !== 'ok') return;
      txns.view.data.transactions = txns.view.data.transactions.map((x) =>
        x.txnKey === t.txnKey ? t : x,
      );
      repaintTransactions(entityId);
    },
    onSignedOut: () => {
      sheet.remove();
      void boot();
    },
    onDismiss: () => {
      sheet.remove();
      // The row was repainted while the sheet was open; focus its new self.
      document
        .querySelector<HTMLElement>(`.txn-row[data-txn-key="${CSS.escape(transaction.txnKey)}"]`)
        ?.focus();
    },
  });
  document.body.append(sheet);
}

async function loadMoney(entityId: string | null): Promise<MoneyState> {
  if (!entityId) return { status: 'none' };
  try {
    return { status: 'ok', data: await envelopeApi.list(entityId) };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) throw err;
    return {
      status: 'error',
      message:
        err instanceof ApiError
          ? err.message
          : "Couldn't reach Ballast. Your figures are untouched.",
    };
  }
}

/** NULL on failure — "couldn't load" is not the same claim as "none". */
async function loadAccounts(): Promise<ApiAccount[] | null> {
  try {
    return (await bankApi.accounts()).accounts;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) throw err;
    return null;
  }
}

async function loadQueue(entityId: string | null): Promise<QueueState> {
  if (!entityId) return { status: 'none' };
  try {
    // BOTH, or neither. A queue built from the suggestions alone would say
    // "Nothing needs you" over deposits nobody has answered for.
    const [{ proposals }, { deposits }, bills] = await Promise.all([
      proposalApi.list(entityId),
      depositApi.list(entityId),
      billApi.list(entityId),
    ]);
    return { status: 'ok', proposals, deposits, bills };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) throw err;
    return {
      status: 'error',
      message:
        err instanceof ApiError ? err.message : "Couldn't reach Ballast. Nothing was decided.",
    };
  }
}

/**
 * Re-fetch the current entity and repaint.
 *
 * Both requests, always, and in parallel. Approving a proposal changes a
 * balance, and funding an envelope changes whether a proposal still fits — so
 * refreshing one without the other leaves the screen internally inconsistent,
 * which is how a queue ends up offering an action the Canvas already knows is
 * impossible.
 */
async function refresh() {
  if (!state) return;
  // Re-read on the next visit to Accounts (v9.0.0); never part of this batch.
  txns = null;
  try {
    const [money, queue, accounts] = await Promise.all([
      loadMoney(state.entityId),
      loadQueue(state.entityId),
      loadAccounts(),
    ]);
    state.money = money;
    state.queue = queue;
    state.accounts = accounts;
    // Only a COMPLETE load counts as fresh. An error state on screen is not
    // "updated just now" -- and neither is an accounts list that did not load.
    if (money.status !== 'error' && queue.status !== 'error' && accounts !== null) {
      state.loadedAt = Date.now();
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    throw err;
  }
  render();
}

/* -------------------------------------------------------------------------
 * Manual sync (v6.0.0) — Settings → "Sync now". Logic in lib/manual-sync.ts.
 * ---------------------------------------------------------------------- */

let syncPhase: SyncPhase = 'idle';
let syncUpdateInstalling = false;
/** What the last Sync now heard from each bank (v7.1.0). Undefined = not asked yet. */
let syncBanks: BankAsk<BankSyncResult[]> | undefined;
/** Repaints whichever Sync card is currently on screen (see renderSync). */
let repaintSync: () => void = () => {};

/** True when a newer build of the app has started installing. */
async function checkForAppUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return false;
  await reg.update();
  return reg.installing !== null || reg.waiting !== null;
}

/**
 * Everything the screens show, in one all-or-nothing load.
 *
 * `loadMoney` and `loadQueue` fold an outage into an `error` STATE so a screen
 * can draw it. A sync must not: applying one would replace figures that were
 * true a minute ago with an error card, for a refresh the operator asked for.
 * So any error rejects, and nothing is applied.
 */
async function loadEverything(requestedEntityId: string | null) {
  const me = await api.me();
  // The entity the operator was looking at — unless it has since gone.
  const entityId = me.entities.some((e) => e.id === requestedEntityId)
    ? requestedEntityId
    : (me.entities[0]?.id ?? null);
  const [money, queue, accounts] = await Promise.all([
    loadMoney(entityId),
    loadQueue(entityId),
    loadAccounts(),
  ]);
  if (money.status === 'error') throw new Error(money.message);
  if (queue.status === 'error') throw new Error(queue.message);
  // loadAccounts folds a failure into null. null is "couldn't load", never
  // "no accounts" -- and a sync must not report fresh over a stale list.
  if (accounts === null) throw new Error("Couldn't load your bank accounts.");
  return { me, entityId, money, queue, accounts };
}

/** What the operator has typed into the rate fields but not yet saved. */
function unsavedRateDrafts(): Array<[string, string]> {
  const entity = currentEntity();
  if (!entity) return [];
  const saved: Record<string, string> = {
    'rate-tax': ppmToPercentText(entity.taxRatePpm),
    'rate-get': ppmToPercentText(entity.getRatePpm),
  };
  const drafts: Array<[string, string]> = [];
  for (const [id, was] of Object.entries(saved)) {
    const input = document.getElementById(id) as HTMLInputElement | null;
    if (input && input.value !== was) drafts.push([id, input.value]);
  }
  return drafts;
}

async function syncNow() {
  if (!state || syncPhase === 'syncing') return;
  const requested = state.entityId;
  syncPhase = 'syncing';
  syncUpdateInstalling = false;
  syncBanks = undefined;
  repaintSync();

  const result = await runManualSync({
    // v7.1.0: the banks first, so the reload below shows what they just said.
    syncBanks: async () => (await connectionApi.syncBanks()).banks,
    fetchAll: () => loadEverything(requested),
    checkForUpdate: checkForAppUpdate,
  });
  // Signed out while it was in flight: nothing left to apply to.
  if (!state) return void (syncPhase = 'idle');

  if (!result.ok) {
    if (result.cause instanceof ApiError && result.cause.status === 401) {
      syncPhase = 'idle';
      return void boot();
    }
    // Nothing was applied, so every figure on screen is exactly what it was.
    syncPhase = 'failed';
    repaintSync();
    return;
  }

  syncPhase = 'ok';
  syncUpdateInstalling = result.updateInstalling;
  syncBanks = result.banks;
  applyLoaded(result.data, requested);
  // render() rebuilt the button the operator just pressed; give focus back.
  document.querySelector<HTMLButtonElement>('.sync-now')?.focus();
}

/**
 * Put an all-or-nothing load on screen. Shared by Sync now and the refresh on
 * return, so the entity-isolation rule below is written once.
 */
function applyLoaded(data: Awaited<ReturnType<typeof loadEverything>>, requested: string | null) {
  if (!state) return;
  const drafts = unsavedRateDrafts();
  const { me, entityId, money, queue, accounts } = data;
  state.me = me;
  // Accounts belong to the operator, not to an entity, so like `me` they are
  // safe to take even if the operator switched entity while this was in flight.
  state.accounts = accounts;
  // ENTITY ISOLATION. If the operator switched entity while this was in
  // flight, the money and queue fetched here belong to the OLD one, and that
  // switch already started its own refresh(). Applying ours would show one
  // LLC's figures under another's name — the commingling this app exists to
  // prevent. Only `me` (entity-independent) is taken in that case.
  if (state.entityId === requested) {
    if (entityId !== requested) state.deciding.clear();
    state.entityId = entityId;
    state.money = money;
    state.queue = queue;
    state.loadedAt = Date.now();
  }
  // A full repaint, like every other action: the Now-Bar's status pill is
  // built from this same state and is on screen in Settings too.
  render();
  for (const [id, value] of drafts) {
    const input = document.getElementById(id) as HTMLInputElement | null;
    if (input) input.value = value;
  }
}

/* -------------------------------------------------------------------------
 * Refresh on return (v7.1.0; owner, 10 Oct 2026). sw.ts always said refresh
 * "happens on app open and visibilitychange"; until now nothing listened. An
 * installed Home Screen app can sit in the background for days, so when it
 * comes back with figures older than a few minutes it re-reads Ballast. Never
 * the bank — that stays the operator's tap on Sync now — and never under the
 * operator's thumb: not with a sheet open, a sync or a decision in flight.
 * ---------------------------------------------------------------------- */

let returning = false;

async function refreshOnReturn() {
  if (!state || returning) return;
  const busy =
    syncPhase === 'syncing' ||
    state.deciding.size > 0 ||
    connecting ||
    reconnecting ||
    document.querySelector('.sheet-backdrop') !== null;
  if (
    !shouldRefreshOnReturn({
      visible: document.visibilityState === 'visible',
      loadedAt: state.loadedAt,
      now: Date.now(),
      busy,
    })
  ) {
    return;
  }
  returning = true;
  const requested = state.entityId;
  try {
    applyLoaded(await loadEverything(requested), requested);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    // Otherwise the screen stays as it was. Its age is still on screen (the
    // freshness line, and "Updated Nm ago" in Settings), so nothing pretends.
  } finally {
    returning = false;
  }
}

document.addEventListener('visibilitychange', () => void refreshOnReturn());
// A page restored from the back-forward cache never fires visibilitychange.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) void refreshOnReturn();
});

function go(view: NavKey) {
  if (!state) return;
  state.view = view;
  render();
}

/* -------------------------------------------------------------------------
 * Tap-to-fund
 * ---------------------------------------------------------------------- */

function openFundSheet(entityId: string, unallocatedId: string, envelope: EnvelopeTileModel) {
  // ONE KEY PER AMOUNT, not one per sheet.
  //
  // A single key for the whole sheet made a retry at a DIFFERENT amount
  // collide with the first attempt's key: the server correctly answered
  // "duplicate", the client read that as success, and the operator was told
  // their $50 had moved when what actually moved was the $200 they had
  // already given up on. Keyed by amount, a retry of the same amount is
  // idempotent and a changed amount is what it actually is — a new request.
  const keys = new Map<number, string>();

  const sheet = createFundSheet({
    envelope,
    availableMinor: unallocatedBalance(),
    onDismiss: () => close(),
    // An ApiError means the Worker replied, and it replied by refusing. Any
    // other rejection is a request that never came back, whose outcome we do
    // not know.
    isRejection: (err) => err instanceof ApiError,
    onConfirm: async (amountMinor) => {
      let key = keys.get(amountMinor);
      if (!key) {
        key = crypto.randomUUID();
        keys.set(amountMinor, key);
      }
      await envelopeApi.transfer(entityId, {
        fromEnvelopeId: unallocatedId,
        toEnvelopeId: envelope.id,
        amountMinor,
        idempotencyKey: key,
      });
      close();
      await refresh();
      announce(`${formatMoney(amountMinor)} set aside for ${envelope.name}.`);
    },
    // Offered only where it means something: an envelope with money in it that
    // is not the residual. Absent, rather than dead, everywhere else.
    onComplete:
      envelope.type === 'unallocated'
        ? undefined
        : async () => {
            const { sweptMinor } = await envelopeApi.complete(entityId, envelope.id);
            close();
            await refresh();
            announce(
              sweptMinor > 0
                ? `${envelope.name} closed. ${formatMoney(sweptMinor)} went back to unallocated.`
                : `${envelope.name} closed.`,
            );
          },
  });

  // Focus restore: whatever opened the sheet gets focus back when it closes,
  // so a keyboard or switch user is not dumped at the top of the document.
  const opener = document.activeElement as HTMLElement | null;
  function close() {
    sheet.remove();
    opener?.focus?.();
  }

  document.body.append(sheet);
}

/**
 * New-envelope sheet.
 *
 * Without this the Canvas could only ever show envelopes someone had inserted
 * into D1 by hand — which made the whole money model unreachable from the app
 * that is supposed to be its only interface.
 */
function openNewEnvelopeSheet(entityId: string) {
  const backdrop = h('div', 'sheet-backdrop');
  const sheet = h('div', 'sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'new-env-title');

  const title = h('h2', 'sheet-title', 'New envelope');
  title.id = 'new-env-title';

  const nameLabel = h('label', 'sheet-field-label', 'Name');
  nameLabel.htmlFor = 'new-env-name';
  const name = h('input', 'sheet-input');
  name.id = 'new-env-name';
  name.type = 'text';
  name.autocomplete = 'off';
  name.placeholder = 'Alignment rack';
  name.maxLength = 80;

  const typeLabel = h('div', 'sheet-field-label', 'What kind');
  const types = h('div', 'sheet-chips');
  types.setAttribute('role', 'radiogroup');
  types.setAttribute('aria-label', 'Envelope kind');
  // 'unallocated' is deliberately absent: it is the residual, created with the
  // entity, and a second one would make the invariant ambiguous.
  const KINDS: Array<{ value: ApiEnvelope['type']; label: string }> = [
    { value: 'tax', label: 'Tax' },
    { value: 'buffer', label: 'Buffer' },
    { value: 'save', label: 'Save' },
    { value: 'spend', label: 'Spend' },
  ];
  let kind: ApiEnvelope['type'] = 'save';
  const kindButtons: HTMLButtonElement[] = [];
  for (const k of KINDS) {
    const b = h('button', 'chip', k.label);
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', String(k.value === kind));
    b.addEventListener('click', () => {
      kind = k.value;
      for (const other of kindButtons) {
        other.setAttribute('aria-checked', String(other === b));
        other.classList.toggle('is-on', other === b);
      }
      // Until the operator picks a zone, it follows the kind.
      if (!zonePicked) showZone(defaultZoneFor(kind));
    });
    b.classList.toggle('is-on', k.value === kind);
    kindButtons.push(b);
    types.append(b);
  }

  /* --- Where it sits (v8.0.0, §12 "optional zone") -----------------------
   * Chips, never typing (§13). Pre-set to the kind's own zone, so ignoring it
   * changes nothing; a project can be filed with the money already claimed, or
   * a reserve with the projects, by one tap.
   * -------------------------------------------------------------------- */
  const zoneLabel = h('div', 'sheet-field-label', 'Where it sits');
  const zones = h('div', 'sheet-chips');
  zones.setAttribute('role', 'radiogroup');
  zones.setAttribute('aria-label', 'Where it sits on the Canvas');
  let zone: string = defaultZoneFor(kind);
  let zonePicked = false;
  const zoneButtons: HTMLButtonElement[] = [];
  const showZone = (id: string) => {
    zone = id;
    for (const b of zoneButtons) {
      const on = b.dataset.zone === id;
      b.setAttribute('aria-checked', String(on));
      b.classList.toggle('is-on', on);
    }
  };
  for (const z of CHOOSABLE_ZONES) {
    const b = h('button', 'chip', z.label);
    b.type = 'button';
    b.dataset.zone = z.id;
    b.setAttribute('role', 'radio');
    b.addEventListener('click', () => {
      zonePicked = true;
      showZone(z.id);
    });
    zoneButtons.push(b);
    zones.append(b);
  }
  showZone(zone);

  const targetLabel = h('label', 'sheet-field-label', 'Target (optional)');
  targetLabel.htmlFor = 'new-env-target';
  const target = h('input', 'sheet-input money');
  target.id = 'new-env-target';
  target.type = 'text';
  target.inputMode = 'decimal';
  target.autocomplete = 'off';
  target.placeholder = '0.00';

  /* --- Due by ------------------------------------------------------------
   * §13: "Setup uses one-tap relative chips... never date-typing." The chips
   * are the path; the date field is the escape hatch for the one obligation
   * that lands on an awkward day. A form that opens on an empty date input is
   * a form an ADHD operator closes again.
   * -------------------------------------------------------------------- */
  const dueLabel = h('div', 'sheet-field-label', 'Due by (optional)');
  const dueChips = h('div', 'sheet-chips');
  const due = h('input', 'sheet-input');
  due.id = 'new-env-due';
  due.type = 'date';
  due.setAttribute('aria-label', 'Due date');

  const dueEcho = h('p', 'soft sheet-note');
  // Reads the choice back in the words the tile will use, so "2026-03-15" is
  // confirmed as "due in 12 days" before it is committed rather than after.
  const echoDue = () => {
    const phrase = due.value ? dueText(due.value) : null;
    dueEcho.textContent = phrase ? `Shows as "${phrase}".` : '';
  };
  due.addEventListener('input', echoDue);

  for (const choice of relativeDateChoices()) {
    const chip = h('button', 'chip', choice.label);
    chip.type = 'button';
    chip.addEventListener('click', () => {
      // A second tap on the chosen chip clears it. Without that the only way
      // to undo a mis-tap is to clear a date field by hand, which is the
      // typing the chips exist to avoid.
      const already = due.value === choice.value;
      due.value = already ? '' : choice.value;
      for (const other of dueChips.children) {
        other.classList.toggle('is-on', !already && other === chip);
      }
      echoDue();
    });
    dueChips.append(chip);
  }

  const message = h('p', 'sheet-message');
  message.setAttribute('role', 'alert');

  const actions = h('div', 'sheet-actions');
  const cancel = h('button', 'btn-quiet', 'Not now');
  cancel.type = 'button';
  const create = h('button', 'btn-primary', 'Create it');
  create.type = 'button';
  actions.append(cancel, create);

  const opener = document.activeElement as HTMLElement | null;
  const close = () => {
    backdrop.remove();
    opener?.focus?.();
  };
  cancel.addEventListener('click', close);

  create.addEventListener('click', async () => {
    const trimmed = name.value.trim();
    if (!trimmed) {
      message.textContent = 'Give it a name so you recognise it on the Canvas.';
      name.focus();
      return;
    }
    let targetMinor: number | null = null;
    if (target.value.trim()) {
      const parsed = parseMoneyToMinor(target.value);
      if (!parsed.ok) {
        message.textContent = PARSE_MESSAGE[parsed.reason];
        target.focus();
        return;
      }
      targetMinor = parsed.minor;
    }
    // The Worker refuses a date it cannot read, which is correct — but being
    // told that after a round trip is worse than being told before it.
    if (due.value && !isValidDateString(due.value)) {
      message.textContent = 'That due date is not a real date.';
      due.focus();
      return;
    }

    create.disabled = true;
    create.textContent = 'Creating…';
    try {
      await envelopeApi.create(entityId, {
        name: trimmed,
        type: kind,
        targetMinor,
        targetDate: due.value || null,
        // NULL when it is the kind's own zone, so "by type" stays the default.
        zone: zone === defaultZoneFor(kind) ? null : zone,
      });
      close();
      await refresh();
      announce(`${trimmed} added.`);
    } catch (err) {
      message.textContent =
        err instanceof ApiError ? err.message : "We couldn't confirm that. Try again.";
      create.disabled = false;
      create.textContent = 'Create it';
    }
  });

  sheet.append(
    title,
    nameLabel,
    name,
    typeLabel,
    types,
    zoneLabel,
    zones,
    targetLabel,
    target,
    dueLabel,
    dueChips,
    due,
    dueEcho,
    message,
    actions,
  );
  backdrop.append(sheet);
  // This sheet claimed aria-modal="true" and had NO focus containment: Tab
  // walked straight out into a Canvas that assistive technology had been told
  // was unavailable. Third sheet, same defect — which is why the trap is now
  // one shared implementation rather than three hand-written ones.
  trapFocus({ backdrop, sheet, onDismiss: close });
  document.body.append(backdrop);
  queueMicrotask(() => name.focus());
}

function unallocatedBalance(): number | null {
  if (state?.money.status !== 'ok') return null;
  return state.money.data.envelopes.find((e) => e.type === 'unallocated')?.balanceMinor ?? null;
}

/** A polite live region, so a committed transfer is announced to somebody. */
function announce(message: string) {
  let region = document.getElementById('ballast-live');
  if (!region) {
    region = h('div');
    region.id = 'ballast-live';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.style.cssText =
      'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
    document.body.append(region);
  }
  region.textContent = message;
}

/* -------------------------------------------------------------------------
 * Views
 * ---------------------------------------------------------------------- */

function currentEntity() {
  if (!state) return undefined;
  return state.me.entities.find((e) => e.id === state!.entityId) ?? state.me.entities[0];
}

function renderHero(): HTMLElement {
  const s = state!;
  const hero = h('div', 'hero');

  // WHOSE money. Entities never commingle (§3), so an unlabelled hero over an
  // account list invites the one misreading the structure exists to prevent.
  const entity = currentEntity();
  if (entity) hero.append(h('div', 'safe-entity', entity.name));
  hero.append(h('div', 'safe-label', 'Safe to spend'));

  // The app's headline figure is its h1. It had no heading role at all, so a
  // screen reader's document outline began at the zone titles.
  const figure = h('h1', 'safe-figure');
  const sub = h('div', 'safe-sub');

  if (s.me.connections.length === 0) {
    figure.textContent = '—';
    sub.textContent = 'Connect an account to see real figures.';
  } else if (s.money.status === 'none') {
    figure.textContent = '—';
    sub.textContent = 'No entity yet.';
  } else if (s.money.status === 'error') {
    figure.textContent = '—';
    sub.textContent = s.money.message;
  } else if (s.money.data.safeToSpendMinor == null) {
    // The "green but dead" rule applied to the hero. If the bank has not said
    // what is available, the honest answer is that we do not know.
    figure.textContent = '—';
    sub.textContent = "Your bank hasn't reported an available balance.";
  } else {
    figure.textContent = formatMoney(s.money.data.safeToSpendMinor);
    sub.textContent = 'Everything else already has a job.';
  }
  // An em dash is announced as nothing at all.
  if (figure.textContent === '—') figure.setAttribute('aria-label', 'not known yet');
  hero.append(figure, sub);

  const worst =
    s.me.connections.find((c) => needsReconnect(c.status)) ??
    s.me.connections.find((c) => c.status === 'pending_disconnect') ??
    s.me.connections[0];
  hero.append(
    createFreshness(
      worst
        ? {
            lastSyncedAt: worst.lastSyncedAt ? Date.parse(worst.lastSyncedAt) : null,
            connection: worst.status,
          }
        : { lastSyncedAt: null, connection: 'never' },
    ),
  );

  return hero;
}

/** The one thing that needs doing, or nothing. Shared by Canvas and Needs You. */
function focalAlert(): HTMLElement | null {
  const s = state!;
  const reauth = s.me.connections.find((c) => needsReconnect(c.status));
  if (reauth) {
    return createFocalAlert({
      title: `Reconnect ${reauth.institutionName ?? 'your bank'}`,
      detail: 'Your bank needs a fresh sign-in before new activity shows up.',
      action: 'Reconnect',
      tone: 'bad',
      // v7.1.0: Plaid update mode. Until then this card had no button, because
      // a button that does nothing is worse than none.
      onAction: () => void startReconnect(reauth.itemId),
    });
  }
  if (s.me.connections.length === 0) {
    return createFocalAlert({
      title: 'Connect your first business account',
      detail: 'Ballast reads balances and transactions. It never moves your money.',
      action: 'Connect',
      tone: 'watch',
      onAction: () => void startConnect(),
    });
  }
  if (s.money.status === 'error') {
    return createFocalAlert({
      title: "Couldn't load your envelopes",
      detail: s.money.message,
      action: 'Try again',
      tone: 'bad',
      onAction: () => void refresh(),
    });
  }

  // LAST, because everything above it is a reason the figures cannot be
  // trusted — and pointing at a deadline computed from stale or missing data
  // would be the app being confidently helpful about numbers it does not have.
  const obligation = attention();
  if (obligation && s.money.status === 'ok') {
    const copy = obligationCopy(obligation);
    const unallocated = s.money.data.envelopes.find((e) => e.type === 'unallocated');
    return createFocalAlert({
      title: copy.title,
      detail: copy.detail,
      action: copy.action,
      // 'watch', never 'bad'. Being part-way through funding a bill that has
      // not arrived yet is the ordinary state of a business, not a failure,
      // and §14 reserves red for things that are actually wrong.
      tone: obligation.tone === 'past' ? 'bad' : 'watch',
      // Only offered when there is somewhere for the money to come FROM. With
      // no unallocated envelope the button would open a sheet that cannot
      // fund anything — a dead control on the one card the screen emphasises.
      onAction: unallocated
        ? () =>
            openFundSheet(s.money.status === 'ok' ? s.money.data.entityId : '', unallocated.id, {
              id: obligation.envelope.id,
              name: obligation.envelope.name,
              type: obligation.envelope.type,
              balanceMinor: obligation.envelope.balanceMinor,
              targetMinor: obligation.envelope.targetMinor,
              targetDate: obligation.envelope.targetDate,
              currency: 'USD',
            })
        : undefined,
    });
  }
  return null;
}

/** The one obligation worth raising right now, or null. */
function attention() {
  const s = state;
  if (!s || s.money.status !== 'ok') return null;
  return nextObligation(
    s.money.data.envelopes.map((e) => ({
      id: e.id,
      name: e.name,
      type: e.type,
      balanceMinor: e.balanceMinor,
      targetMinor: e.targetMinor,
      targetDate: e.targetDate,
      currency: 'USD',
    })),
  );
}

/**
 * The live drag-to-fund wiring, if any. Its listeners sit on `document`, and
 * every render() rebuilds the Canvas, so the previous wiring is removed before
 * a new one is made — otherwise each repaint would leave three listeners behind.
 */
let disposeDrag: (() => void) | null = null;

function toTileModel(e: ApiEnvelope): EnvelopeTileModel {
  return {
    id: e.id,
    name: e.name,
    type: e.type,
    // NOT `?? 0`. An unknown balance stays unknown all the way to the tile.
    balanceMinor: e.balanceMinor,
    targetMinor: e.targetMinor,
    targetDate: e.targetDate,
    zone: e.zone,
    currency: 'USD',
  };
}

function renderCanvas(body: HTMLElement) {
  const s = state!;

  const onboarding = renderOnboarding();
  if (onboarding) body.append(onboarding);

  const alert = focalAlert();
  if (alert) body.append(alert);

  if (s.money.status !== 'ok') return;
  const money = s.money.data;

  if (money.overAllocatedMinor > 0) {
    const notice = h('div', 'over-allocated');
    notice.append(
      document.createTextNode('Allocations are '),
      Object.assign(document.createElement('strong'), {
        textContent: formatMoney(money.overAllocatedMinor),
      }),
      document.createTextNode(
        ' above the cash actually available. Nothing is wrong with your envelopes — the bank balance moved. Free some up when you can.',
      ),
    );
    body.append(notice);
  }

  if (money.envelopes.length === 0) return;

  const tiles: EnvelopeTileModel[] = money.envelopes.map(toTileModel);

  const unallocated = money.envelopes.find((e) => e.type === 'unallocated');
  const fundable = unallocated != null && (unallocated.balanceMinor ?? 0) > 0;

  const grid = h('div');
  body.append(grid);

  grid.append(
    createZoneGrid({
      zones: groupIntoZones(tiles),
      // Unallocated is the SOURCE of a fund, never its destination, and there
      // is nothing to fund from when it is empty or unknown.
      isSelectable: (envelope) => envelope.type !== 'unallocated' && fundable,
      onSelect: (envelope) => {
        // Unallocated is the SOURCE of a fund, never its destination, so it is
        // not a control. createEnvelopeTile renders non-interactive tiles as
        // plain elements, so this guard is belt and braces.
        if (envelope.type === 'unallocated' || !unallocated || !fundable) return;
        openFundSheet(money.entityId, unallocated.id, envelope);
      },
    }),
  );

  // Drag to fund (v8.0.0, §13): an enhancement over the tap above, opening the
  // same sheet. Only when there is something to fund from.
  const source = grid.querySelector<HTMLElement>('.tile[data-type="unallocated"]');
  if (source && fundable && unallocated) {
    disposeDrag = enableDragToFund({
      source,
      canDrop: (id) => tiles.some((t) => t.id === id && t.type !== 'unallocated'),
      onDrop: (id) => {
        const target = tiles.find((t) => t.id === id);
        if (target) openFundSheet(money.entityId, unallocated.id, target);
      },
    });
  }

  // The allocation waterfall. Rendered only when it would stage something:
  // free cash that is known and positive, and at least one envelope short of
  // a target. No dead controls.
  const shortOfTarget = money.envelopes.some(
    (e) =>
      e.type !== 'unallocated' &&
      e.targetMinor != null &&
      e.balanceMinor != null &&
      e.balanceMinor < e.targetMinor,
  );
  if (fundable && shortOfTarget && unallocated.balanceMinor != null) {
    const fill = h(
      'button',
      'btn-quiet waterfall-btn',
      `Fill envelopes from ${formatMoney(unallocated.balanceMinor)} free`,
    );
    fill.type = 'button';
    fill.style.cssText = 'width:100%;min-height:var(--tap);margin-top:14px';
    fill.addEventListener('click', () => void stageWaterfall(money.entityId, fill));
    body.append(fill);
  }

  // The fill order. Only with two or more envelopes the waterfall can fill —
  // an order of one is not a choice, and a control with nothing behind it is a
  // dead control. Independent of whether there is free cash today: the order is
  // a standing preference, not part of running the waterfall.
  const orderable = fillOrder(money.envelopes);
  if (orderable.length >= 2) {
    const order = h('button', 'btn-quiet fill-order-btn', 'Fill order');
    order.type = 'button';
    order.style.cssText = 'width:100%;min-height:var(--tap);margin-top:14px';
    order.setAttribute('aria-label', 'Fill order: choose which envelope is filled first');
    order.addEventListener('click', () => openFillOrderSheet(money.entityId, orderable));
    body.append(order);
  }

  body.append(renderRunway(money));

  const add = h('button', 'btn-quiet', '+ New envelope');
  add.type = 'button';
  add.style.cssText = 'width:100%;min-height:var(--tap);margin-top:14px';
  add.addEventListener('click', () => openNewEnvelopeSheet(money.entityId));
  body.append(add);
}

/**
 * Stage the waterfall, then show the operator what it suggested. Nothing moves
 * here — the suggestions land in Needs You and each is approved on its own.
 */
async function stageWaterfall(entityId: string, button: HTMLButtonElement) {
  if (button.disabled) return;
  button.disabled = true;
  try {
    const { staged } = await proposalApi.planWaterfall(entityId);
    announce(
      staged === 1
        ? '1 suggestion is waiting in Needs you.'
        : `${staged} suggestions are waiting in Needs you.`,
    );
    if (state) state.view = 'needs';
    await refresh();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    button.disabled = false;
    announce(err instanceof ApiError ? err.message : "Couldn't reach Ballast. Nothing was staged.");
  }
}

/**
 * Runway: how many months the buffer would last at the normal monthly draw.
 *
 * NULL is the normal case until a bank supplies income history, and it renders
 * as an em dash with the reason — never as a dial at zero, which would read as
 * "you have no runway" when the truth is "we can't measure it yet".
 */
function renderRunway(money: EnvelopesResponse): HTMLElement {
  const months = money.runwayMonths;
  const draw = money.monthlyDraw;
  const card = h('section', 'card vitals runway');
  card.append(h('div', 'label', 'Runway'));
  if (months == null) {
    const figure = h('div', 'runway-figure', '\u2014');
    figure.setAttribute('aria-label', 'not known yet');
    const hasBuffer = money.envelopes.some((e) => e.type === 'buffer');
    card.append(
      figure,
      h(
        'p',
        'soft',
        !hasBuffer
          ? 'Add a Buffer envelope to measure how long it would last.'
          : draw == null
            ? anyImporting(state!.me.connections.map((c) => c.historyStatus))
              ? 'Your bank is still sending its history. It will not be estimated meanwhile.'
              : 'Needs three full months of bank history. It will not be estimated.'
            : 'No spending recorded to measure against.',
      ),
    );
  } else {
    card.append(
      createGauge({
        value: months,
        min: 0,
        max: 12,
        target: 3,
        bands: [
          { from: 0, to: 1, tone: 'bad' },
          { from: 1, to: 3, tone: 'watch' },
          { from: 3, to: 12, tone: 'good' },
        ],
        label: 'months of runway',
        sublabel: draw
          ? `at ${formatMoney(draw.drawMinor)}/mo \u00b7 ${
              draw.seasonal
                ? '12-month average'
                : `${draw.basisMonths}-month average, not a full year`
            }`
          : undefined,
      }),
    );
  }
  card.style.cssText = 'padding:14px 15px;margin-top:14px';
  return card;
}

/**
 * Decide one proposal.
 *
 * The optimism is deliberately ONE-WAY. The card is removed from the queue
 * immediately so the screen never offers the same decision twice, but no
 * balance is touched on screen until the server has confirmed — the figures
 * come from `refresh()`, never from arithmetic done here. Guessing a balance
 * locally is how a UI ends up disagreeing with the ledger, and in this app the
 * ledger is the only thing that is true.
 */
async function decide(proposal: ApiProposal, action: 'approve' | 'dismiss') {
  const s = state;
  if (!s || !s.entityId) return;
  // A second tap while the first is in flight. The server refuses it anyway —
  // that is what the idempotency key is for — but a 409 the operator did not
  // cause should never reach them.
  if (s.deciding.has(proposal.id)) return;

  s.deciding.add(proposal.id);
  if (s.queue.status === 'ok') {
    s.queue = {
      ...s.queue,
      proposals: s.queue.proposals.filter((p) => p.id !== proposal.id),
    };
  }
  render();

  try {
    if (action === 'approve') {
      await proposalApi.approve(s.entityId, proposal.id);
      announce(
        `${formatMoney(proposal.amountMinor)} moved to ${proposal.to.name ?? 'the envelope'}.`,
      );
    } else {
      await proposalApi.dismiss(s.entityId, proposal.id);
      announce('Dismissed.');
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    // Put it back. A proposal that failed to commit is still pending, and
    // silently dropping it would leave the operator believing they had decided
    // something they had not.
    announce(
      err instanceof ApiError ? err.message : "Couldn't reach Ballast. Nothing was decided.",
    );
  } finally {
    s.deciding.delete(proposal.id);
    // The authority, either way: whatever the server now says is the queue.
    await refresh();
  }
}

/**
 * Answer "what was this deposit?" (v7.0.0, Blueprint §6).
 *
 * No optimism, unlike decide(): the answer decides whether money is set aside
 * for tax, and the server may also teach a sender, answer the sender's other
 * deposits and stage or withdraw a set-aside. All of that is the server's to
 * say, so the screen shows what it returns rather than guessing at it.
 *
 * The in-flight set is keyed by txnKey so a second tap on the same deposit
 * cannot fire, and it shares `deciding` with proposals because nothing else
 * uses a txnKey as an id.
 */
async function answerDeposit(deposit: ApiDeposit, type: ApiInflowType) {
  const s = state;
  if (!s || !s.entityId) return;
  if (s.deciding.has(deposit.txnKey)) return;

  s.deciding.add(deposit.txnKey);
  try {
    const result = await depositApi.classify(s.entityId, deposit.txnKey, type);
    announce(classifyMessage(result));
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    announce(
      err instanceof ApiError
        ? err.message
        : "Couldn't reach Ballast. Nothing about this deposit changed.",
    );
  } finally {
    s.deciding.delete(deposit.txnKey);
    // The authority, either way: the server now says what the queue is.
    await refresh();
  }
}

/* -------------------------------------------------------------------------
 * Bills (v8.0.0, Blueprint §9). Like answerDeposit: no optimism — the server
 * creates the envelope or moves the cycle's reserve, so the screen shows what
 * it returns. In-flight ids share `deciding` (bill ids are random, never a
 * proposal id or a txnKey).
 * ---------------------------------------------------------------------- */

async function billAction(id: string, run: (entityId: string) => Promise<string>) {
  const s = state;
  if (!s || !s.entityId || s.deciding.has(id)) return;
  s.deciding.add(id);
  try {
    announce(await run(s.entityId));
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    announce(err instanceof ApiError ? err.message : "Couldn't reach Ballast. Nothing changed.");
  } finally {
    s.deciding.delete(id);
    await refresh();
  }
}

function trackBill(bill: ApiBill) {
  // The bank gave no amount: the operator says what it costs, never $0.
  if (bill.amountMinor == null) return openTrackBillSheet(bill);
  return billAction(bill.id, async (entityId) => {
    const r = await billApi.track(entityId, bill.id);
    return `${r.name} is tracked: ${formatMoney(r.targetMinor)}${r.targetDate ? `, ${dueText(r.targetDate)}` : ''}.`;
  });
}

function dismissBill(bill: ApiBill) {
  return billAction(bill.id, async (entityId) => {
    await billApi.dismiss(entityId, bill.id);
    return `${bill.name} won't be asked about again.`;
  });
}

function settleBill(paid: ApiPaidBill) {
  return billAction(paid.id, async (entityId) => {
    const r = await billApi.paid(entityId, paid.id);
    const back = r.clearedMinor > 0 ? `${formatMoney(r.clearedMinor)} is back in free cash. ` : '';
    return `${back}${paid.envelopeName}: ${r.nextDueDate ? `next one ${dueText(r.nextDueDate)}` : 'no next date yet'}.`;
  });
}

/** "Add the amount" for a bill the bank sent without one. */
function openTrackBillSheet(bill: ApiBill) {
  const backdrop = h('div', 'sheet-backdrop');
  const sheet = h('div', 'sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'track-bill-title');

  const title = h('h2', 'sheet-title', `Track ${bill.name}`);
  title.id = 'track-bill-title';
  const sub = h(
    'p',
    'sheet-sub',
    "Your bank didn't say what this bill costs. What should Ballast set aside for each one?",
  );
  const label = h('label', 'sheet-field-label', 'Amount each time');
  label.htmlFor = 'track-bill-amount';
  const amount = h('input', 'sheet-input money');
  amount.id = 'track-bill-amount';
  amount.type = 'text';
  amount.inputMode = 'decimal';
  amount.autocomplete = 'off';
  amount.placeholder = '0.00';

  const message = h('p', 'sheet-message');
  message.setAttribute('role', 'alert');
  const actions = h('div', 'sheet-actions');
  const cancel = h('button', 'btn-quiet', 'Not now');
  cancel.type = 'button';
  const confirm = h('button', 'btn-primary', 'Track it');
  confirm.type = 'button';
  actions.append(cancel, confirm);

  const opener = document.activeElement as HTMLElement | null;
  const close = () => {
    backdrop.remove();
    opener?.focus?.();
  };
  cancel.addEventListener('click', close);
  confirm.addEventListener('click', () => {
    const parsed = parseMoneyToMinor(amount.value);
    if (!parsed.ok) {
      message.textContent = PARSE_MESSAGE[parsed.reason];
      amount.focus();
      return;
    }
    close();
    void billAction(bill.id, async (entityId) => {
      const r = await billApi.track(entityId, bill.id, { amountMinor: parsed.minor });
      return `${r.name} is tracked: ${formatMoney(r.targetMinor)}${r.targetDate ? `, ${dueText(r.targetDate)}` : ''}.`;
    });
  });

  sheet.append(title, sub, label, amount, message, actions);
  backdrop.append(sheet);
  trapFocus({ backdrop, sheet, onDismiss: close });
  document.body.append(backdrop);
  queueMicrotask(() => amount.focus());
}

/**
 * Change which envelope the waterfall fills first (v7.0.0).
 *
 * Saving moves no money and stages nothing; it rewrites the order and
 * re-fetches, so the Canvas and the next waterfall agree.
 */
function openFillOrderSheet(entityId: string, envelopes: readonly ApiEnvelope[]) {
  const sheet = createFillOrderSheet({
    envelopes,
    onDismiss: () => close(),
    onSave: async (orderedIds) => {
      try {
        await envelopeApi.setOrder(entityId, orderedIds);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        throw err;
      }
      close();
      await refresh();
      announce('Fill order saved. Nothing has moved.');
    },
  });

  const opener = document.activeElement as HTMLElement | null;
  function close() {
    sheet.remove();
    opener?.focus?.();
  }

  document.body.append(sheet);
}

/**
 * Change a proposal's amount before deciding on it (§4: proposals are
 * editable).
 *
 * Saving moves NOTHING. It rewrites the suggestion and re-fetches, which is
 * what makes a $2,400 waterfall against $1,520 recoverable — the card comes
 * back with an Approve button on it instead of being a dead end whose only
 * exit is declining something the operator wanted.
 */
function openProposalEditSheet(entityId: string, proposal: ApiProposal) {
  const sheet = createProposalEditSheet({
    proposal,
    onDismiss: () => close(),
    // An ApiError means the Worker replied, and it replied by refusing.
    isRejection: (err) => err instanceof ApiError,
    onConfirm: async (amountMinor) => {
      await proposalApi.edit(entityId, proposal.id, amountMinor);
      close();
      await refresh();
      announce(`Changed to ${formatMoney(amountMinor)}. Nothing has moved.`);
    },
  });

  // Focus restore: whatever opened the sheet gets it back, so a keyboard or
  // switch user is not dumped at the top of the document.
  const opener = document.activeElement as HTMLElement | null;
  function close() {
    sheet.remove();
    opener?.focus?.();
  }

  document.body.append(sheet);
}

/**
 * Change the operator's password (v6.1.0).
 *
 * The Worker ends every OTHER session and keeps this one, so on success there is
 * nothing to re-authenticate: the sheet closes and the card says what happened.
 * A 401 is the one different case — the session itself ended while the sheet was
 * open — and goes to the sign-in screen like every other screen's 401 does.
 */
function openPasswordSheet(opener: HTMLElement, status: HTMLElement) {
  const sheet = createPasswordSheet({
    onDismiss: () => close(),
    describeError: passwordChangeErrorText,
    onSubmit: async (currentPassword, newPassword) => {
      let result;
      try {
        result = await api.changePassword(currentPassword, newPassword);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        throw err;
      }
      close();
      const text = passwordChangedText(result.otherSessionsRevoked);
      status.textContent = text;
      announce(text);
    },
  });

  function close() {
    sheet.remove();
    opener.focus();
  }

  document.body.append(sheet);
}

function renderNeeds(body: HTMLElement) {
  const s = state!;

  // The focal alert outranks the queue. A bank that needs reconnecting makes
  // every suggestion below it stale, so it is shown first and on its own.
  const alert = focalAlert();
  if (alert) body.append(alert);

  if (s.queue.status === 'error') {
    body.append(
      createFocalAlert({
        title: "Couldn't load what needs you",
        detail: s.queue.message,
        action: 'Try again',
        tone: 'bad',
        onAction: () => void refresh(),
      }),
    );
    return;
  }

  const proposals = s.queue.status === 'ok' ? s.queue.proposals : [];
  const allDeposits = s.queue.status === 'ok' ? s.queue.deposits : [];
  // Open questions first, because the answer decides whether a set-aside is
  // suggested at all; the suggestions below may change once they are answered.
  const toLookAt = depositsNeedingALook(allDeposits);
  const answered = allDeposits.filter((d) => d.inflowType != null);
  const waiting = s.queue.status === 'ok' ? queueCount(s.queue) : 0;

  // The view's h1, always, and carrying the count rather than a static word.
  // The Canvas's h1 is its hero figure; this screen had no heading element at
  // all, which leaves a screen-reader user no way to tell where they are.
  const heading = h(
    'h1',
    'label',
    // "Nothing needs you" is a claim, and it is only ours to make once the
    // queue has actually been fetched. With no entity selected there is
    // nothing to have checked.
    s.queue.status === 'ok' ? queuePillText(waiting) : 'Needs you',
  );
  heading.style.cssText = 'margin:4px 2px 2px';
  body.append(heading);

  for (const deposit of toLookAt) {
    body.append(createDepositCard(deposit, { onAnswer: (d, type) => void answerDeposit(d, type) }));
  }

  // v8.0.0: paid bills first — good news, one tap — then bills to answer.
  const bills = s.queue.status === 'ok' ? s.queue.bills : { suggestions: [], paid: [] };
  for (const paid of bills.paid) {
    body.append(createPaidBillCard(paid, (p) => void settleBill(p)));
  }
  for (const bill of bills.suggestions) {
    body.append(
      createBillCard(bill, {
        onTrack: (b) => void trackBill(b),
        onDismiss: (b) => void dismissBill(b),
      }),
    );
  }

  if (waiting === 0) {
    const card = h('section', 'card vitals');
    // Only said when it has actually been checked. `status: 'none'` means no
    // entity is selected, which is not the same as all-clear.
    card.append(
      h(
        'p',
        'soft',
        s.queue.status === 'ok'
          ? 'Nothing right now. Balances are labelled the way you left them.'
          : 'Select an entity to see what needs you.',
      ),
    );
    card.style.cssText = 'padding:14px 15px';
    body.append(card);
  } else {
    for (const proposal of proposals) {
      body.append(
        createProposalCard(proposal, {
          onApprove: (p) => void decide(p, 'approve'),
          onDismiss: (p) => void decide(p, 'dismiss'),
          onEdit: (p) => openProposalEditSheet(s.entityId!, p),
        }),
      );
    }
  }

  // The forward view (§9): what is due in the next 30 days and how much of it is
  // set aside — bills tracked from the bank and deadlines set by hand alike.
  if (s.money.status === 'ok') {
    const ahead = forwardView(s.money.data.envelopes.map(toTileModel));
    if (ahead) body.append(createForwardCard(ahead));
  }

  // The record of answers already given — folded, because the open questions
  // above are what needs the operator. It is also the only place a LEARNED
  // answer can be seen and changed.
  const recent = createRecentDeposits(answered, {
    onAnswer: (d, type) => void answerDeposit(d, type),
    open: recentDepositsOpen,
    onToggle: (open) => {
      recentDepositsOpen = open;
    },
  });
  if (recent) body.append(recent);

  // The promise the queue is making, stated rather than implied. It is the
  // reason this screen can be trusted with an Approve button at all.
  const note = h('section', 'card vitals');
  note.append(h('div', 'label', 'How this works'));
  note.append(
    h(
      'p',
      'soft',
      'Nothing here has changed a balance. Each one is checked against your real balance at the moment you approve it \u2014 not when it was suggested.',
    ),
  );
  body.append(note);
}

function renderAccounts(body: HTMLElement) {
  const s = state!;

  const vitals = h('section', 'card vitals');
  vitals.append(h('div', 'label', 'Entities'));

  if (s.me.entities.length === 0) {
    vitals.append(
      h('p', 'soft', 'No entities yet. Each linked account is tagged to one entity and its state.'),
    );
  } else {
    for (const ent of s.me.entities) {
      // Every entity is SELECTABLE when there is more than one. The Canvas was
      // hard-wired to entities[0], so the other LLCs were listed as text the
      // operator could never open — in an app whose whole premise is keeping
      // them separate.
      const selectable = s.me.entities.length > 1;
      const row = h(selectable ? 'button' : 'div');
      if (selectable) {
        (row as HTMLButtonElement).type = 'button';
        row.addEventListener('click', () => {
          state!.entityId = ent.id;
          state!.view = 'canvas';
          // The old entity's queue must not survive the switch: showing one
          // LLC's proposals under another's name is the commingling the whole
          // app is structured to prevent, and it would be on screen.
          state!.queue = { status: 'none' };
          state!.deciding.clear();
          void refresh();
        });
      }
      row.style.cssText =
        'display:flex;justify-content:space-between;align-items:center;width:100%;' +
        'min-height:var(--tap);background:none;border:0;padding:0;font:inherit;' +
        'color:var(--ink);text-align:left;' +
        (selectable ? 'cursor:pointer' : '');
      const name = h('span', undefined, ent.name);
      if (ent.id === s.entityId) name.style.fontWeight = '700';
      row.append(name, h('span', 'soft num', ent.state));
      vitals.append(row);
    }
  }

  vitals.append(h('div', 'rule'));

  const connSummary = h('div', 'duo');
  const numStyle =
    'font-family:var(--display),system-ui,sans-serif;font-weight:700;font-size:1.9rem;letter-spacing:-.03em';

  const left = h('div');
  left.append(h('div', 'label', 'Accounts'));
  const accountsNum = h('div', 'num');
  accountsNum.style.cssText = numStyle;
  accountsNum.textContent = String(s.me.connections.reduce((n, c) => n + c.accountCount, 0));
  left.append(accountsNum);

  const right = h('div');
  right.append(h('div', 'label', 'Connections'));
  const connNum = h('div', 'num');
  connNum.style.cssText = numStyle;
  connNum.textContent = String(s.me.connections.length);
  right.append(connNum);

  connSummary.append(left, h('div', 'vsep'), right);
  vitals.append(connSummary);

  body.append(vitals);
  if (s.me.connections.length) body.append(renderConnections());
  body.append(renderBankAccounts());
  // v9.0.0: the entity's transactions, where receipts are attached.
  if (s.entityId) body.append(renderTransactions(s.entityId));
}

/**
 * Each bank connection, after linking (v7.1.0): how fresh it is, whether its
 * history is still arriving, and what can be done with it. Reconnect appears
 * only when it would help (no dead controls); Disconnect opens a sheet that says
 * what it will do before anything happens.
 */
function renderConnections(): HTMLElement {
  const s = state!;
  const card = h('section', 'card vitals connections');
  card.style.cssText = 'padding:14px 15px;margin-top:14px';
  card.append(h('div', 'label', 'Bank connections'));
  for (const c of s.me.connections) {
    const row = h('div', 'connection');
    row.dataset.itemId = c.itemId;
    const name = c.institutionName ?? 'Bank';
    const f = computeFreshness({
      lastSyncedAt: c.lastSyncedAt ? Date.parse(c.lastSyncedAt) : null,
      connection: c.status,
    });
    const head = h('div', 'connection-head');
    const status = h('span', 'soft connection-status', f.text);
    status.dataset.state = f.state;
    head.append(h('span', undefined, name), status);
    row.append(head);
    if (f.detail && f.state === 'disconnected')
      row.append(h('p', 'soft connection-history', f.detail));
    const history = historyNote(c.historyStatus);
    if (history) row.append(h('p', 'soft connection-history', `${capitalise(history)}.`));

    const actions = h('div', 'connection-actions');
    if (canReconnect(c.status)) {
      const reconnect = h('button', 'btn-primary', 'Reconnect');
      reconnect.type = 'button';
      reconnect.setAttribute('aria-label', `Reconnect ${name}`);
      reconnect.addEventListener('click', () => void startReconnect(c.itemId));
      actions.append(reconnect);
    }
    const disconnect = h('button', 'btn-quiet', 'Disconnect');
    disconnect.type = 'button';
    disconnect.setAttribute('aria-label', `Disconnect ${name}`);
    disconnect.addEventListener('click', () => void openDisconnectSheet(c));
    actions.append(disconnect);
    row.append(actions);
    card.append(row);
  }
  const msg = h('p', 'soft connection-msg');
  msg.setAttribute('role', 'status');
  card.append(msg);
  return card;
}

const capitalise = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

function connectionMessage(text: string) {
  announce(text);
  const msg = document.querySelector('.connection-msg');
  if (msg) msg.textContent = text;
}

let reconnecting = false;

/**
 * Reconnect a bank (Plaid update mode). The server mints the link token from
 * the stored access token and, once Link finishes, marks the connection healthy
 * and syncs it; if the bank still refuses, the status that comes back says so.
 */
async function startReconnect(itemId: string) {
  if (reconnecting || !state) return;
  reconnecting = true;
  const name =
    state.me.connections.find((c) => c.itemId === itemId)?.institutionName ?? 'your bank';
  try {
    const status = await reconnectBank(itemId);
    if (status == null) return; // Link closed: nothing changed.
    state.me = await api.me();
    await refresh();
    connectionMessage(
      status === 'ok'
        ? `Reconnected ${name}.`
        : `${name} still needs you to sign in. Nothing else changed.`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    connectionMessage(
      err instanceof ApiError ? err.message : "Couldn't reconnect. Nothing changed.",
    );
  } finally {
    reconnecting = false;
  }
}

/** Disconnect and purge (A8), behind a sheet that says what it will do first. */
async function openDisconnectSheet(connection: ConnectionSummary) {
  const opener = document.activeElement as HTMLElement | null;
  let preview;
  try {
    preview = await connectionApi.disconnectPreview(connection.itemId);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    connectionMessage(
      err instanceof ApiError ? err.message : "Couldn't reach Ballast. Nothing was deleted.",
    );
    return;
  }

  const sheet = createDisconnectSheet({
    preview,
    onDismiss: () => close(),
    onConfirm: async () => {
      try {
        await connectionApi.disconnect(connection.itemId);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        throw err;
      }
      sheet.remove();
      if (!state) return;
      state.me = await api.me();
      await refresh();
      connectionMessage(
        `Disconnected ${connection.institutionName ?? 'the bank'}. Its accounts and transactions were deleted.`,
      );
      // The row (and its button) are gone; land somewhere that still exists.
      document
        .querySelector<HTMLElement>(
          '.connections .connection-actions button, .bank-accounts button',
        )
        ?.focus();
    },
  });
  function close() {
    sheet.remove();
    opener?.focus?.();
  }
  document.body.append(sheet);
}

let connecting = false;

/**
 * Connect a bank. On success the new accounts land UNASSIGNED, so the
 * operator is taken straight to the screen that assigns them — nothing is
 * counted toward any entity until they say so.
 */
async function startConnect() {
  if (connecting || !state) return;
  connecting = true;
  try {
    const linked = await connectBank();
    if (!linked) return;
    state.me = await api.me();
    state.view = 'accounts';
    announce(
      `Connected ${linked.institutionName ?? 'your bank'}. Choose which entity each account belongs to.`,
    );
    await refresh();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    announce(err instanceof ApiError ? err.message : "Couldn't connect. Nothing was saved.");
    const msg = document.querySelector('.bank-msg');
    if (msg)
      msg.textContent =
        err instanceof ApiError ? err.message : "Couldn't connect. Nothing was saved.";
  } finally {
    connecting = false;
  }
}

/** Each linked account: which entity it belongs to, and whether it counts. */
function renderBankAccounts(): HTMLElement {
  const s = state!;
  const card = h('section', 'card vitals bank-accounts');
  card.append(h('div', 'label', 'Bank accounts'));
  const accounts = s.accounts ?? [];

  if (s.accounts == null) {
    card.append(h('p', 'soft', "Couldn't load your bank accounts."));
  } else if (accounts.length === 0) {
    card.append(h('p', 'soft', 'No bank connected yet.'));
  } else {
    card.append(
      h(
        'p',
        'soft',
        'An account counts toward an entity only once you assign it. Unassigned accounts are counted nowhere.',
      ),
    );
  }

  for (const a of accounts) {
    const row = h('div', 'bank-account');
    row.style.cssText = 'padding:10px 0;border-top:1px solid var(--cardln)';
    const head = h('div');
    head.style.cssText = 'display:flex;justify-content:space-between;gap:12px';
    head.append(
      h('span', undefined, `${a.name}${a.mask ? ` ••${a.mask}` : ''}`),
      // NOT `?? 0`: an unreported balance is unknown.
      h('span', 'num', a.availableMinor == null ? '\u2014' : formatMoney(a.availableMinor)),
    );
    row.append(head);
    if (a.institutionName) row.append(h('div', 'soft', a.institutionName));

    const controls = h('div');
    controls.style.cssText =
      'display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-top:8px';
    const select = h('select');
    // Unique per account: two LLCs can each have an "Operating" account, and
    // two identical labels leave a screen-reader user unable to tell them apart.
    const who = [a.name, a.mask ? `••${a.mask}` : null, a.institutionName]
      .filter(Boolean)
      .join(' ');
    select.setAttribute('aria-label', `Entity for ${who}`);
    select.style.cssText =
      'min-height:var(--tap);padding:0 10px;border-radius:12px;font:inherit;flex:1 1 12em;' +
      'border:1px solid var(--cardln);background:transparent;color:var(--ink)';
    const none = h('option', undefined, 'Not assigned');
    none.value = '';
    select.append(none);
    for (const e of s.me.entities) {
      const o = h('option', undefined, e.name);
      o.value = e.id;
      if (e.id === a.entityId) o.selected = true;
      select.append(o);
    }

    const toggle = h('label');
    toggle.style.cssText = 'display:flex;align-items:center;gap:8px;min-height:var(--tap)';
    const box = h('input');
    box.type = 'checkbox';
    box.checked = a.budgetable;
    box.style.cssText = 'width:22px;height:22px';
    toggle.append(box, h('span', undefined, 'Counts toward safe to spend'));

    const save = async () => {
      select.disabled = true;
      box.disabled = true;
      try {
        await bankApi.assign(a.id, { entityId: select.value || null, budgetable: box.checked });
        announce('Account updated.');
        await refresh();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return void boot();
        announce(err instanceof ApiError ? err.message : "Couldn't save. Nothing changed.");
        select.disabled = false;
        box.disabled = false;
      }
    };
    select.addEventListener('change', () => void save());
    box.addEventListener('change', () => void save());
    controls.append(select, toggle);
    row.append(controls);
    card.append(row);
  }

  const msg = h('p', 'soft bank-msg');
  msg.setAttribute('role', 'status');
  const connect = h(
    'button',
    'btn-quiet',
    accounts.length ? 'Connect another bank' : 'Connect a bank',
  );
  connect.type = 'button';
  connect.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
  connect.addEventListener('click', () => void startConnect());
  card.append(msg, connect);
  card.style.cssText = 'padding:14px 15px;margin-top:14px';
  return card;
}

function renderSettings(body: HTMLElement) {
  const themeCard = h('section', 'card vitals');
  themeCard.append(h('div', 'label', 'Appearance'));
  const group = h('div');
  group.setAttribute('role', 'radiogroup');
  group.setAttribute('aria-label', 'Theme');
  group.style.cssText = 'display:flex;gap:8px;margin-top:10px;flex-wrap:wrap';

  const options: Array<{ value: ThemePreference; label: string }> = [
    { value: 'system', label: 'System' },
    { value: 'atelier', label: 'Atelier · light' },
    { value: 'graphite', label: 'Graphite · dark' },
  ];
  const current = readPreference();
  for (const opt of options) {
    const b = h('button', undefined, opt.label);
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', String(opt.value === current));
    b.style.cssText =
      'min-height:var(--tap);padding:8px 14px;border-radius:99px;cursor:pointer;font:inherit;' +
      `border:1px solid ${opt.value === current ? 'var(--accent)' : 'var(--cardln)'};` +
      'background:transparent;color:var(--ink)';
    b.addEventListener('click', () => {
      applyTheme(opt.value);
      render();
    });
    group.append(b);
  }
  themeCard.append(group);
  body.append(themeCard);

  body.append(renderSync());
  body.append(renderNotifications());
  body.append(renderRates());
  body.append(renderBusinesses());
  body.append(renderPasskeys());

  const account = h('section', 'card vitals');
  account.append(h('div', 'label', 'Account'));
  account.append(h('p', 'soft', state!.me.user.email));
  // Status line for the password change. Sits in the card so the confirmation is
  // on screen after the sheet closes, not only in a live region.
  const pwStatus = h('p', 'soft password-status');
  pwStatus.setAttribute('role', 'status');
  pwStatus.style.cssText = 'margin-top:8px;min-height:1.2em';
  const changePw = h('button', 'btn-quiet change-password', 'Change password');
  changePw.type = 'button';
  changePw.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
  changePw.addEventListener('click', () => openPasswordSheet(changePw, pwStatus));
  account.append(changePw, pwStatus);

  const out = h('button', 'btn-quiet', 'Sign out');
  out.type = 'button';
  out.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
  out.addEventListener('click', async () => {
    out.disabled = true;
    // A signed-out phone must stop receiving this account's notifications:
    // Ballast forgets the device first (best effort, never blocking sign-out).
    await Promise.race([
      forgetPushDevice(),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]).catch(() => {});
    try {
      await api.logout();
    } catch {
      // Signing out locally still matters even if the request failed.
    }
    state = null;
    renderLogin(() => void boot());
  });
  account.append(out);
  body.append(account);
}

/**
 * The manual sync control (v6.0.0; asks the banks since v7.1.0).
 *
 * The copy states what it does: asks each connected bank for news (each bank
 * at most once every few minutes), then refreshes the screen from Ballast. Until
 * v7.1.0 it said "It doesn't contact your bank", and this text and
 * e2e/sync.spec.ts changed in the same commit when that stopped being true.
 * Each bank's result is its own line: one bank's trouble never hides another's.
 */
function renderSync(): HTMLElement {
  const card = h('section', 'card vitals sync');
  card.append(h('div', 'label', 'Sync'));
  card.append(
    h(
      'p',
      'soft',
      'Asks each connected bank for anything new, then refreshes this screen and checks for a newer version of the app. Each bank can be asked once every 5 minutes.',
    ),
  );

  const status = h('p', 'soft sync-status');
  status.setAttribute('role', 'status');
  status.style.cssText = 'margin-top:8px;min-height:1.2em';

  const button = h('button', 'btn-quiet sync-now', 'Sync now');
  button.type = 'button';
  button.style.cssText = 'margin-top:8px;min-height:var(--tap);padding:0 18px;width:100%';
  button.addEventListener('click', () => void syncNow());

  const banks = h('ul', 'bank-sync-results');

  const paint = () => {
    const d = describeSync({
      phase: syncPhase,
      loadedAt: state?.loadedAt ?? Date.now(),
      updateInstalling: syncUpdateInstalling,
    });
    status.textContent = d.text;
    banks.replaceChildren();
    if (syncPhase === 'ok' && syncBanks) {
      const lines = syncBanks.ok
        ? syncBanks.results.map((r) => describeBankSync(r))
        : [{ text: BANKS_UNREACHABLE, tone: 'watch' as const }];
      for (const line of lines) {
        const li = h('li', undefined, line.text);
        li.dataset.tone = line.tone;
        banks.append(li);
      }
    }
    // idle | syncing | ok | failed — how e2e/sync.spec.ts knows a sync is over.
    card.dataset.phase = syncPhase;
    // Disabled while in flight so a second tap cannot start a second sync.
    button.disabled = d.busy;
    button.setAttribute('aria-busy', String(d.busy));
  };
  // A sync can finish after the operator has left Settings and come back. The
  // card that started it is gone; whichever one is on screen now must repaint.
  repaintSync = paint;
  paint();

  card.append(status, banks, button);
  return card;
}

/**
 * Set-aside rates for the current entity. Each deposit that lands in one of
 * its budgetable accounts stages a tax_skim proposal at these rates. Blank is
 * NOT SET — no skim — which is deliberately different from 0%.
 */
function renderRates(): HTMLElement {
  const entity = currentEntity();
  const card = h('section', 'card vitals rates');
  card.append(h('div', 'label', 'Set-aside rates'));
  if (!entity) {
    card.append(h('p', 'soft', 'No entity yet.'));
    return card;
  }
  card.append(
    h(
      'p',
      'soft',
      `For ${entity.name}. Every deposit suggests setting this much aside in Tax. Nothing moves until you approve it.`,
    ),
  );

  const field = (id: string, label: string, ppm: number | null) => {
    const wrap = h('label', 'rate-field');
    wrap.htmlFor = id;
    wrap.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:10px';
    const input = h('input');
    input.id = id;
    input.inputMode = 'decimal';
    input.autocomplete = 'off';
    input.placeholder = 'not set';
    input.value = ppmToPercentText(ppm);
    input.style.cssText =
      'width:7.5em;min-height:var(--tap);padding:0 12px;border-radius:12px;font:inherit;text-align:right;' +
      'border:1px solid var(--cardln);background:transparent;color:var(--ink)';
    const right = h('span');
    right.style.cssText = 'display:flex;align-items:center;gap:6px';
    right.append(input, h('span', 'soft', '%'));
    wrap.append(h('span', undefined, label), right);
    card.append(wrap);
    return input;
  };
  const tax = field('rate-tax', 'Income tax', entity.taxRatePpm);
  const get = field('rate-get', 'GET / sales tax', entity.getRatePpm);

  const msg = h('p', 'soft rate-msg');
  msg.setAttribute('role', 'status');
  msg.style.cssText = 'margin-top:8px;min-height:1.2em';
  const save = h('button', 'btn-quiet', 'Save rates');
  save.type = 'button';
  save.style.cssText = 'margin-top:8px;min-height:var(--tap);padding:0 18px;width:100%';
  save.addEventListener('click', async () => {
    const t = parsePercentToPpm(tax.value);
    const g = parsePercentToPpm(get.value);
    if (!t.ok || !g.ok) {
      msg.textContent = (!t.ok ? t : (g as { message: string })).message;
      return;
    }
    save.disabled = true;
    try {
      const saved = await envelopeApi.setRates(entity.id, { taxRatePpm: t.ppm, getRatePpm: g.ppm });
      entity.taxRatePpm = saved.taxRatePpm;
      entity.getRatePpm = saved.getRatePpm;
      msg.textContent = 'Saved.';
      announce('Set-aside rates saved.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return void boot();
      msg.textContent =
        err instanceof ApiError ? err.message : "Couldn't reach Ballast. Nothing changed.";
    } finally {
      save.disabled = false;
    }
  });
  card.append(msg, save);
  return card;
}

/* -------------------------------------------------------------------------
 * Shell
 * ---------------------------------------------------------------------- */

function render() {
  // Whatever view comes next, the previous Canvas's drag wiring goes with it.
  disposeDrag?.();
  disposeDrag = null;
  const s = state;
  if (!s) return;

  // Keep the reader where they were. A full repaint that jumps to the top
  // punishes anyone who tapped a control near the bottom of the page.
  const prior = app.querySelector<HTMLElement>('.scroll')?.scrollTop ?? 0;

  clear(app);

  const scroll = h('main', 'scroll');
  scroll.id = 'main';
  const body = h('div', 'body');

  if (s.view === 'canvas') scroll.append(renderHero());

  switch (s.view) {
    case 'canvas':
      renderCanvas(body);
      break;
    case 'needs':
      renderNeeds(body);
      break;
    case 'accounts':
      renderAccounts(body);
      break;
    case 'settings':
      renderSettings(body);
      break;
  }

  const foot = h('p', 'soft');
  foot.style.cssText = 'font-size:.75rem;text-align:center;padding:8px 0 4px';
  // Precise, because the fund sheet on the next screen says "Set it aside".
  // Ballast moves LABELS between envelopes; it never moves cash between
  // accounts, and the two claims must not look like they contradict.
  foot.textContent = `Ballast ${VERSION} · read-only · never moves money between your accounts`;
  body.append(foot);

  scroll.append(body);

  const reauth = s.me.connections.find((c) => needsReconnect(c.status));
  // The ONE living status. It has to agree with the body: a pill reading
  // "Nothing needs you" above a notice saying otherwise teaches the operator
  // that the pill is decoration.
  const pill = reauth
    ? { text: 'Reconnect a bank', status: 'bad' as const }
    : s.me.connections.length === 0
      ? { text: 'Connect an account', status: 'watch' as const }
      : s.money.status === 'error'
        ? { text: "Couldn't load figures", status: 'bad' as const }
        : s.money.status === 'ok' && s.money.data.overAllocatedMinor > 0
          ? { text: 'Over-allocated', status: 'watch' as const }
          : s.money.status === 'ok' && s.money.data.safeToSpendMinor == null
            ? { text: 'Waiting on your bank', status: 'watch' as const }
            : s.queue.status === 'error'
              ? { text: "Couldn't load the queue", status: 'bad' as const }
              : // The count, from the SAME function the Needs You heading
                // uses. Two places computing "3 need you" independently is
                // how a pill ends up one decision behind the screen it
                // describes.
                s.queue.status === 'ok' && queueCount(s.queue) > 0
                ? { text: queuePillText(queueCount(s.queue)), status: 'watch' as const }
                : // An underfunded bill inside the window is something that
                  // needs them, so "Nothing needs you" would be false — and
                  // the pill is visible from every screen, including the one
                  // showing the card that says otherwise.
                  attention()
                  ? { text: 'Something is due', status: 'watch' as const }
                  : { text: 'Nothing needs you', status: 'good' as const };

  app.append(scroll, createNowBar({ active: s.view, pill, onNavigate: go }));

  const next = app.querySelector<HTMLElement>('.scroll');
  if (next) next.scrollTop = prior;
}

/* -------------------------------------------------------------------------
 * First run (v10.0.0): the Getting started card. Steps in lib/onboarding.ts.
 * ---------------------------------------------------------------------- */

/** Opened from the Home Screen, not a Safari tab. */
function isInstalled(): boolean {
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    matchMedia('(display-mode: standalone)').matches
  );
}

function renderOnboarding(): HTMLElement | null {
  const s = state!;
  const input = {
    entities: s.me.entities,
    connections: s.me.connections.length,
    accounts: s.accounts?.map((a) => ({ entityId: a.entityId })) ?? null,
    installed: isInstalled(),
    onboardedAt: s.me.user.onboardedAt ?? null,
  };
  if (!showOnboarding(input)) return null;

  const card = h('section', 'card vitals onboarding');
  card.setAttribute('aria-labelledby', 'onboarding-title');
  const title = h('h2', 'label', 'Getting started');
  title.id = 'onboarding-title';
  card.append(title);

  const list = h('ol', 'onb-steps');
  for (const step of onboardingSteps(input)) list.append(onboardingRow(step));
  card.append(list);

  const hide = h('button', 'btn-quiet onb-hide', 'Hide this');
  hide.type = 'button';
  hide.addEventListener('click', async () => {
    hide.disabled = true;
    try {
      await onboardingApi.done();
      state!.me.user.onboardedAt = new Date().toISOString();
      render();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return void boot();
      hide.disabled = false;
      announce("Couldn't hide it. Try again.");
    }
  });
  card.append(hide);
  return card;
}

const ONBOARDING_ACTIONS: Partial<Record<OnboardingStep['key'], string>> = {
  business: 'Add business',
  bank: 'Connect a bank',
  assign: 'Assign accounts',
  rates: 'Set rates',
};

function onboardingRow(step: OnboardingStep): HTMLElement {
  const li = h('li', step.done ? 'onb-step is-done' : 'onb-step');
  li.dataset.step = step.key;
  const mark = h('span', 'onb-mark', step.done ? '✓' : '');
  mark.setAttribute('aria-hidden', 'true');
  const text = h('div', 'onb-text');
  const name = h('div', 'onb-title', step.title);
  if (step.done) {
    const sr = h('span', 'sr-only', ' (done)');
    name.append(sr);
  }
  text.append(name, h('p', 'soft onb-detail', step.detail));
  li.append(mark, text);

  const label = ONBOARDING_ACTIONS[step.key];
  if (step.ready && label) {
    const go = h('button', 'btn-quiet onb-go', label);
    go.type = 'button';
    go.addEventListener('click', () => runOnboardingStep(step.key, go));
    text.append(go);
  }
  return li;
}

function runOnboardingStep(key: OnboardingStep['key'], opener: HTMLButtonElement) {
  switch (key) {
    case 'business':
      return openBusinessSheet(opener);
    case 'bank':
      // The same Connect as the Accounts screen, from there, so its messages
      // (including "Bank connections are not set up yet") land where they belong.
      go('accounts');
      return void startConnect();
    case 'assign':
      go('accounts');
      document.querySelector<HTMLElement>('.bank-accounts select')?.focus();
      return;
    case 'rates':
      go('settings');
      document.getElementById('rate-tax')?.focus();
      return;
  }
}

function openBusinessSheet(opener: HTMLElement) {
  const sheet = createBusinessSheet({
    onDismiss: () => close(),
    describeError: (err) =>
      err instanceof ApiError ? err.message : "Couldn't add it. Nothing was saved.",
    onSubmit: async (name, stateCode) => {
      let created;
      try {
        created = await onboardingApi.addBusiness(name, stateCode);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        throw err;
      }
      close();
      if (!state) return;
      state.me = await api.me();
      // The new business is the one on screen: it is what the next steps are about.
      state.entityId = created.entity.id;
      state.deciding.clear();
      announce(`Added ${created.entity.name}.`);
      await refresh();
    },
  });
  function close() {
    sheet.remove();
    if (opener.isConnected) opener.focus();
  }
  document.body.append(sheet);
}

/** Settings → Businesses: the same Add business, so it is not onboarding-only. */
function renderBusinesses(): HTMLElement {
  const s = state!;
  const card = h('section', 'card vitals businesses');
  card.append(h('div', 'label', 'Businesses'));
  card.append(
    h(
      'p',
      'soft',
      s.me.entities.length === 0
        ? 'No business yet.'
        : s.me.entities.map((e) => `${e.name} (${e.state})`).join(' · '),
    ),
  );
  const add = h('button', 'btn-quiet add-business', 'Add a business');
  add.type = 'button';
  add.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
  add.addEventListener('click', () => openBusinessSheet(add));
  card.append(add);
  return card;
}

/* -------------------------------------------------------------------------
 * Push notifications (v10.0.0). The lifecycle is in lib/push-client.ts; what
 * is sent, and when, is the Worker's (src/push/notify.ts).
 * ---------------------------------------------------------------------- */

/** What the server said (null = not asked yet, or the ask failed). */
let pushInfo: PushInfo | null = null;
let pushInfoFailed = false;
let pushSubscribed = false;
let pushBusy = false;
let pushNote = '';
let pushRegistration: ServiceWorkerRegistration | null = null;
let pushCheckedAt = 0;
let pushLoading = false;
/* -------------------------------------------------------------------------
 * Passkeys (v11.0.0) — Settings card
 *
 * Fetched ONCE and repainted from the cache: v10.0.0 shipped a card that asked
 * the server again on every repaint of Settings (docs/STATE.md), and the Sync
 * now request-set test is what caught it. Reloaded only after a change.
 * ---------------------------------------------------------------------- */

let passkeyList: PasskeyList | null = null;
let passkeyLoad: Promise<void> | null = null;
let passkeyLoadFailed = false;
let passkeyNote = '';
let repaintPasskeys: () => void = () => {};

function loadPasskeys(): Promise<void> {
  passkeyLoad = passkeyApi
    .list()
    .then((list) => {
      passkeyList = list;
      passkeyLoadFailed = false;
    })
    .catch((err) => {
      if (err instanceof ApiError && err.status === 401) return void boot();
      passkeyLoadFailed = true;
    })
    .finally(() => repaintPasskeys());
  return passkeyLoad;
}

function renderPasskeys(): HTMLElement {
  const card = h('section', 'card vitals passkeys');

  const paint = () => {
    clear(card);
    card.append(h('div', 'label', 'Passkeys'));
    const list = passkeyList;
    if (!list) {
      card.append(h('p', 'soft', passkeyLoadFailed ? "Couldn't load your passkeys." : 'Loading…'));
      if (passkeyLoadFailed) {
        const retry = h('button', 'btn-quiet passkey-retry', 'Try again');
        retry.type = 'button';
        retry.addEventListener('click', () => {
          passkeyLoadFailed = false;
          paint();
          void loadPasskeys();
        });
        card.append(retry);
      } else if (!passkeyLoad) {
        void loadPasskeys();
      }
      return;
    }

    if (list.passkeys.length === 0) {
      card.append(
        h(
          'p',
          'soft',
          'Sign in with your iPhone’s passcode instead of typing your password — from Bitwarden, from the iPhone’s own Passwords, or both. Your password keeps working too.',
        ),
      );
    } else {
      const ul = h('ul', 'passkey-list');
      for (const p of list.passkeys) {
        const li = h('li', 'passkey-row');
        const text = h('div', 'passkey-text');
        text.append(
          h('div', 'passkey-name', p.name),
          h('div', 'soft passkey-meta', passkeyMeta(p, new Date())),
        );
        const remove = h('button', 'btn-quiet passkey-remove', 'Remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove passkey ${p.name}`);
        remove.addEventListener('click', () => openRemovePasskeySheet(remove, p));
        li.append(text, remove);
        ul.append(li);
      }
      card.append(ul);
    }

    if (!list.available) {
      card.append(
        h(
          'p',
          'soft passkey-elsewhere',
          list.rpId
            ? `Passkeys work only at ${list.rpId}. Open Ballast there to add or use one.`
            : "Passkeys aren't set up on the server. Your password works.",
        ),
      );
    } else if (!passkeysSupported(window)) {
      card.append(h('p', 'soft', "This browser can't make passkeys. Your password works."));
    } else if (list.passkeys.length < list.max) {
      const add = h('button', 'btn-quiet passkey-add', 'Add a passkey');
      add.type = 'button';
      add.addEventListener('click', () => openAddPasskeySheet(add));
      card.append(add);
    }

    const status = h('p', 'soft passkey-status', passkeyNote);
    status.setAttribute('role', 'status');
    card.append(status);
  };

  repaintPasskeys = () => {
    if (card.isConnected) paint();
  };
  paint();
  return card;
}

function openAddPasskeySheet(opener: HTMLElement) {
  let options: CreationOptionsJSON | null = null;
  let name = '';
  const sheet = createPasskeySheet({
    mode: 'add',
    describeError: passkeyEnrolErrorText,
    onDismiss: () => close(),
    onContinue: async (currentPassword, typedName) => {
      try {
        options = await passkeyApi.enrolOptions(currentPassword);
        name = typedName;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        throw err;
      }
    },
    onSave: async () => {
      // FIRST await after the tap: the platform call (passkey-sheet.ts).
      const cred = await navigator.credentials.create(creationOptions(options!));
      if (!cred) throw new DOMException('No passkey was saved.', 'NotAllowedError');
      let added;
      try {
        added = await passkeyApi.add(name, registrationJSON(cred as unknown as CredentialLike));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        // The challenge is spent whatever the answer: start again from the password.
        throw new RestartEnrolment(err);
      }
      close();
      passkeyNote = `Added “${added.name}”. Next time, tap “Sign in with a passkey”.`;
      announce(passkeyNote);
      await loadPasskeys();
    },
  });

  function close() {
    sheet.remove();
    if (opener.isConnected) opener.focus();
  }
  document.body.append(sheet);
}

function openRemovePasskeySheet(opener: HTMLElement, passkey: PasskeySummary) {
  const sheet = createPasskeySheet({
    mode: 'remove',
    passkeyName: passkey.name,
    describeError: passkeyEnrolErrorText,
    onDismiss: () => close(),
    onRemove: async (currentPassword) => {
      try {
        await passkeyApi.remove(passkey.id, currentPassword);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          close();
          return void boot();
        }
        // Already gone (removed on another device): the list is what is wrong.
        if (!(err instanceof ApiError && err.status === 404)) throw err;
      }
      close();
      passkeyNote = `Removed “${passkey.name}”. Your password still works.`;
      announce(passkeyNote);
      await loadPasskeys();
    },
  });

  function close() {
    sheet.remove();
    if (opener.isConnected) opener.focus();
  }
  document.body.append(sheet);
}

let repaintNotifications: () => void = () => {};

function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

const pushFlag: DeviceFlag = {
  get() {
    try {
      return localStorage.getItem('ballast.push') === 'on';
    } catch {
      return false;
    }
  },
  set(on) {
    try {
      if (on) localStorage.setItem('ballast.push', 'on');
      else localStorage.removeItem('ballast.push');
    } catch {
      // Storage unavailable: the card simply offers Turn on again next time.
    }
  },
};

const pushServer: PushServer = {
  save: (subscription) => pushApi.save(subscription),
  remove: (endpoint) => pushApi.remove(endpoint),
};

function pushManager(): PushManagerLike | null {
  return (pushRegistration?.pushManager as unknown as PushManagerLike | undefined) ?? null;
}

/** Ask the server and the browser where things stand. Never prompts. */
async function loadPush(): Promise<void> {
  try {
    pushInfo = await pushApi.info();
    pushInfoFailed = false;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    pushInfoFailed = true;
  }
  await loadPushRegistration();
}

/**
 * The browser's half: the worker registration and whether it holds a
 * subscription. No network. Only in the installed app — a tab has no push, so
 * there is nothing to look up (and looking on every repaint was a request each).
 */
async function loadPushRegistration(): Promise<void> {
  if (!pushSupported() || !isInstalled()) return;
  try {
    // ready never settles without a worker, so it is bounded.
    pushRegistration =
      (await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
      ])) ?? null;
    pushSubscribed = Boolean(await pushManager()?.getSubscription());
  } catch {
    pushSubscribed = false;
  }
}

/**
 * On every open, and on coming back to the app: keep this device's
 * subscription alive. Safari drops subscriptions silently and never says so.
 */
async function maintainPush() {
  pushCheckedAt = Date.now();
  await loadPush();
  const pm = pushManager();
  if (pushInfo?.available && pushInfo.publicKey && pm) {
    try {
      const outcome = await ensureSubscription({
        pushManager: pm,
        server: pushServer,
        publicKey: pushInfo.publicKey,
        permission: Notification.permission,
        flag: pushFlag,
      });
      if (outcome === 'kept' || outcome === 'recreated' || outcome === 'rekeyed') {
        pushSubscribed = true;
      }
    } catch {
      // Left as it was; the card shows the browser's own state.
    }
  }
  repaintNotifications();
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && state && Date.now() - pushCheckedAt > 600_000) {
    void maintainPush();
  }
});

/** Sign-out: Ballast forgets this device, then the browser does. */
async function forgetPushDevice() {
  const pm = pushManager();
  if (pm) await turnOff({ pushManager: pm, server: pushServer, flag: pushFlag });
}

function renderNotifications(): HTMLElement {
  const card = h('section', 'card vitals notifications');
  const paint = () => {
    clear(card);
    card.append(h('div', 'label', 'Notifications'));

    if (pushInfo == null) {
      card.append(
        h('p', 'soft', pushInfoFailed ? "Couldn't load notification settings." : 'Checking…'),
      );
      return;
    }
    const status = pushStatus({
      serverAvailable: pushInfo.available,
      installed: isInstalled(),
      supported: pushSupported(),
      permission: pushSupported() ? Notification.permission : 'default',
      subscribed: pushSubscribed,
    });

    const say = (text: string) => card.append(h('p', 'soft push-state', text));
    switch (status) {
      case 'server-off':
        say("Notifications aren't set up on the server yet.");
        return;
      case 'not-installed':
        say(
          'Notifications reach you only from the Home Screen app. In Safari, tap Share, then Add to Home Screen, and open Ballast from there.',
        );
        return;
      case 'unsupported':
        say("This browser can't receive notifications.");
        return;
      case 'blocked':
        say(
          'Notifications are switched off for Ballast. To turn them on, open your iPhone’s Settings → Notifications → Ballast, then come back.',
        );
        return;
    }

    card.append(
      h(
        'p',
        'soft',
        'A notification says only what happened — “Something needs you” — never an amount, an account or a name.',
      ),
    );

    const note = h('p', 'soft push-note');
    note.setAttribute('role', 'status');
    note.textContent = pushNote;

    if (status === 'off') {
      const on = h(
        'button',
        'btn-quiet push-on',
        pushBusy ? 'Turning on…' : 'Turn on notifications',
      );
      on.type = 'button';
      on.disabled = pushBusy;
      on.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
      on.addEventListener('click', () => void turnOnPush());
      card.append(on, note);
      return;
    }

    say('On for this device.');
    const prefs = pushInfo.prefs ?? { needsYou: true, reconnect: true, bills: true };
    const toggles: Array<{ key: keyof PushPrefs; label: string }> = [
      { key: 'needsYou', label: 'Something needs you' },
      { key: 'reconnect', label: 'A bank needs reconnecting' },
      { key: 'bills', label: 'A bill is coming due' },
    ];
    for (const t of toggles) {
      const row = h('label', 'push-toggle');
      row.style.cssText =
        'display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:var(--tap);margin-top:4px';
      const box = h('input');
      box.type = 'checkbox';
      box.checked = prefs[t.key];
      box.style.cssText = 'width:22px;height:22px;accent-color:var(--accent)';
      box.addEventListener('change', () => void setPushPref(t.key, box.checked));
      row.append(h('span', undefined, t.label), box);
      card.append(row);
    }

    const test = h('button', 'btn-quiet push-test', 'Send a test notification');
    test.type = 'button';
    test.disabled = pushBusy;
    test.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
    test.addEventListener('click', () => void sendTestPush());

    const off = h('button', 'btn-quiet push-off', 'Turn off on this device');
    off.type = 'button';
    off.disabled = pushBusy;
    off.style.cssText = 'margin-top:8px;min-height:var(--tap);padding:0 18px;width:100%';
    off.addEventListener('click', () => void turnOffPush());
    card.append(test, off, note);
  };
  repaintNotifications = () => {
    if (card.isConnected) paint();
  };
  paint();
  // The card draws at once and fills in when the server and browser answer.
  // The server is asked ONCE (boot already did, usually); a repaint never asks
  // again — e2e/notifications.spec.ts "repainting Settings" pins that.
  if (!pushLoading && (pushInfo == null || (pushRegistration == null && isInstalled()))) {
    pushLoading = true;
    void (pushInfo == null ? loadPush() : loadPushRegistration()).finally(() => {
      pushLoading = false;
      repaintNotifications();
    });
  }
  return card;
}

async function turnOnPush() {
  // subscribe() must be the FIRST await after the tap, or iOS refuses the
  // prompt without showing it (push-client.ts). The key is already loaded.
  const pm = pushManager();
  if (!pm || !pushInfo?.publicKey) {
    pushNote = "Couldn't reach the app's background worker. Close Ballast and open it again.";
    return repaintNotifications();
  }
  pushBusy = true;
  pushNote = '';
  const started = turnOn({
    pushManager: pm,
    server: pushServer,
    publicKey: pushInfo.publicKey,
    flag: pushFlag,
  });
  repaintNotifications();
  try {
    await started;
    pushSubscribed = true;
    pushNote = 'Notifications are on.';
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    pushNote =
      Notification.permission === 'denied'
        ? ''
        : err instanceof ApiError
          ? err.message
          : "Couldn't turn notifications on. Try again.";
  } finally {
    pushBusy = false;
  }
  repaintNotifications();
  announce(pushNote || 'Notifications were not turned on.');
}

async function turnOffPush() {
  const pm = pushManager();
  if (!pm) return;
  pushBusy = true;
  repaintNotifications();
  try {
    await turnOff({ pushManager: pm, server: pushServer, flag: pushFlag });
    pushSubscribed = false;
    pushNote = 'Notifications are off on this device.';
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    pushNote = "Couldn't turn them off. Try again.";
  } finally {
    pushBusy = false;
  }
  repaintNotifications();
  announce(pushNote);
}

async function setPushPref(key: keyof PushPrefs, on: boolean) {
  if (!pushInfo?.prefs) return;
  const next = { ...pushInfo.prefs, [key]: on };
  try {
    pushInfo.prefs = (await pushApi.setPrefs(next)).prefs;
    pushNote = 'Saved.';
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    pushNote = "Couldn't save that. Nothing changed.";
  }
  repaintNotifications();
}

async function sendTestPush() {
  pushBusy = true;
  pushNote = '';
  repaintNotifications();
  try {
    const r = await pushApi.test();
    pushNote =
      r.sent > 0
        ? `Sent to ${r.sent === 1 ? 'your device' : `${r.sent} devices`}. It should arrive in a few seconds.`
        : r.gone > 0
          ? 'This device’s subscription had expired. Close Ballast and open it again, then retry.'
          : "Couldn't send it. Try again in a minute.";
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return void boot();
    pushNote = err instanceof ApiError ? err.message : "Couldn't send it. Try again.";
  } finally {
    pushBusy = false;
  }
  repaintNotifications();
  announce(pushNote);
}

/** A notification opens the app at /?view=…; take it once, then tidy the URL. */
function viewFromUrl(): NavKey {
  const views: NavKey[] = ['canvas', 'needs', 'accounts', 'settings'];
  const asked = new URLSearchParams(location.search).get('view');
  if (asked) history.replaceState(null, '', location.pathname);
  return views.includes(asked as NavKey) ? (asked as NavKey) : 'canvas';
}

/* -------------------------------------------------------------------------
 * Boot
 * ---------------------------------------------------------------------- */

function renderFatal(message: string) {
  clear(app);
  const scroll = h('div', 'scroll');
  const body = h('div', 'body');
  const card = h('section', 'card vitals');
  card.append(h('h2', undefined, "Can't reach Ballast"));
  card.append(h('p', 'soft', message));
  const retry = h('button', 'btn-quiet', 'Try again');
  retry.type = 'button';
  retry.style.cssText = 'margin-top:12px;min-height:var(--tap);padding:0 18px;width:100%';
  retry.addEventListener('click', () => void boot());
  card.append(retry);
  body.append(card);
  scroll.append(body);
  app.append(scroll);
}

async function boot() {
  initTheme();
  watchSystemTheme(() => {});

  try {
    const me = await api.me();
    const entityId = me.entities[0]?.id ?? null;
    const [money, queue, accounts] = await Promise.all([
      loadMoney(entityId),
      loadQueue(entityId),
      loadAccounts(),
    ]);
    // A fresh session starts with a clean slate: a "couldn't reach Ballast"
    // left over from the last one must not greet the next sign-in.
    syncPhase = 'idle';
    syncUpdateInstalling = false;
    state = {
      me,
      view: viewFromUrl(),
      entityId,
      money,
      queue,
      deciding: new Set(),
      accounts,
      loadedAt: Date.now(),
    };
    render();
    void maintainPush();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      renderLogin(() => void boot());
    } else {
      renderFatal(
        err instanceof ApiError
          ? err.message
          : 'The app is offline, or the server is not responding. Your data is untouched.',
      );
    }
  }
}

trackWindowHeight();
void boot();

/* -------------------------------------------------------------------------
 * Service worker
 * ---------------------------------------------------------------------- */

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Absolute path: a relative register('sw.js') from a nested route resolves
    // to /nested/sw.js and 404s, and the default scope is the directory the
    // script was served from.
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
      // Non-fatal — the app works without offline support.
    });
  });

  // The service worker calls skipWaiting() + clients.claim(), which means a
  // freshly-activated worker can end up controlling a page running the OLD
  // bundle — and on standalone iOS there is no address bar, so the operator
  // cannot hard-refresh their way out of it. Reload exactly once when control
  // changes. The guard matters: without it, claim() during the initial
  // registration would reload the page on every first visit.
  // `reloading` alone is a fire-once guard, not a "was this page already
  // controlled" guard — so on the FIRST install, clients.claim() changes the
  // controller and reloads the page, which is exactly the case this is meant
  // to avoid. Capture whether a controller existed at registration time.
  const wasControlled = navigator.serviceWorker.controller !== null;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!wasControlled || reloading) return;
    reloading = true;
    window.location.reload();
  });
}

/**
 * Ask for storage persistence once installed.
 *
 * Home Screen web apps are already exempt from ITP's 7-day eviction, so this
 * is defence in depth rather than the fix — but it costs nothing and protects
 * the shell cache if the app is opened from a Safari tab instead.
 */
if (navigator.storage?.persist) {
  const installed =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    matchMedia('(display-mode: standalone)').matches;
  if (installed) void navigator.storage.persist().catch(() => {});
}
